// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import { TableActionButton, TableActionGroup } from "@channelplay/table-actions";
import { SearchFilterBar } from '../components/SearchFilterBar';
import { BlockLoader, InlineLoader } from '../components/loading';
import ConfirmDeleteActions from '../components/ConfirmDeleteActions';
import { ContentLoadingState } from '../components/ContentLoadingState';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus,
  Edit2,
  Trash2,
  X,
  Loader2,
  ClipboardList,
  ClipboardCheck,
  ClipboardX,
  MessageSquare,
  AlertCircle,
  GripVertical,
  Camera,
  Paperclip,
  Repeat,
  ChevronDown,
  ChevronRight,
  Zap,
  ArrowRightLeft,
  Package,
  Sparkles,
} from 'lucide-react';
import { tasksAPI, rolesAPI, productMasterAPI, categoriesAPI, storeTradesAPI, type TaskAssignees } from '../services/api';
import { usePageLine } from '../hooks/usePageLine';
import { useLinkedSearch, type SearchLink } from '../hooks/useLinkedSearch';
import MultiSearchableSelect from '../components/MultiSearchableSelect';
import {
  NO_AUDIENCE_OPTIONS,
  audienceFieldsFrom,
  audienceJoinLabel,
  audienceMatchOf,
  audienceValues,
  countAudienceKinds,
  describeTaskAudience,
  optionsWithPicked,
  pickedEmployeesOf,
  retiredOptionsOf,
  type AudienceKind,
  type AudienceMatch,
  type AudienceOptions,
} from '../utils/taskAudience';
import { useAuthStore } from '../stores/authStore';
import RecurrenceSelector from '../components/RecurrenceSelector';
import { TaskRecurrence, RecurrencePattern, defaultsForPattern, formatRecurrenceText, validateRecurrence } from '../types/recurrence';
import RuleBuilder, { summarizeRule, type RuleGroup, type RuleQuestion } from '../components/RuleBuilder';
import OptionSourceEditor, { StaticOptionsEditor, normalizeOptionSource, type OptionSourceType, type OptionSource, type ProductMasterSource, type EditorProductCatalog } from '../components/OptionSourceEditor';
import { useOutsideClick } from '../hooks/useOutsideClick';
import { balancePhrases, type BalancePhrases } from '../utils/balanceLabel';
import { splitTasksByStatus, type TaskListTab } from '../utils/taskListFilter';

type TaskStatus = 'active' | 'inactive';
type QuestionType =
  | 'text'
  | 'number'
  | 'single_choice'
  | 'multiple_choice'
  | 'dropdown'
  | 'date'
  | 'photo'
  // File-attachment answer — mobile uploads a PDF/DOC/DOCX/XLS/XLSX/JPEG/PNG via the
  // document picker; the blob path reuses the photo_url column (UI keys off question_type).
  | 'file'
  // Employee Rating capability — constrained mobile pickers. Their wiring is
  // identified purely by these question_type values (no per-question flags).
  | 'rating_1_5'
  | 'store_employee_picker'
  // The task's product picker. System-managed: the API adds and removes it in
  // step with the task's product_mode, so it is deliberately absent from
  // `questionTypes` below — it must never be offered as an Answer Type.
  // It lives in the question list purely so it can be DRAGGED between questions
  // and given a visibility rule, which is the whole point of it being a row.
  | 'product_selection';

type SubmissionMode = 'overwrite' | 'incremental';
/** The most people a task can be hand-picked for (the server's limit). More than this is "everyone who fits". */
const MAX_PICKED_EMPLOYEES = 2000;
/** Rows drawn at once in the Assigned Employees pop-up; the search narrows the rest. */
const ASSIGNEE_ROWS_SHOWN = 200;

interface Task extends Partial<TaskRecurrence> {
  id: string;
  title: string;
  description: string | null;
  task_type: string;
  status: TaskStatus;
  recurrence: string; // Deprecated field
  start_date: string | null;
  end_date: string | null;
  created_by: number | null;
  created_at: string;
  question_count: number;
  response_count: number;
  target_role_ids?: string[];
  // Further audience conditions; empty = none (see utils/taskAudience).
  target_store_category_ids?: number[];
  target_store_trade_ids?: string[];
  target_am_codes?: string[];
  target_th_codes?: string[];
  target_audience_match?: string;
  target_employee_codes?: string[];
  submission_mode?: SubmissionMode;
  geo_based_submission?: boolean;
}

interface ValidationRules {
  format?: 'letters' | 'numbers' | 'alphanumeric' | 'email' | 'integer' | 'positive' | 'regex' | null;
  regex?: string | null;
  length?: { min?: number | null; max?: number | null } | null;
  dateFormat?: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' | 'DD-MMM-YYYY' | 'long' | null;
  unique?: { scope: 'question' | 'task' | 'project' | 'global' } | null;
  errorMessage?: string | null;
}

interface QuestionRules {
  visibility?: RuleGroup;
  option_source?: OptionSource;
  validation?: ValidationRules;
}

interface Question {
  id: string;
  task_id: string;
  question_text: string;
  question_type: QuestionType;
  options: string[] | null;
  is_required: boolean;
  display_order: number;
  rules: QuestionRules | null;
}

/**
 * What a question does to the running stock balance (Phase 4).
 *
 * This one dropdown is the whole mechanism by which separate tasks affect each
 * other. A Monday stock-count task and a daily sales task never reference one
 * another — they both write against the same (store, SKU) coordinates, and this
 * says how each contributes. Balances are DERIVED from those answers, so a
 * correction to an answer is a correction to the balance.
 */
type StockEffect = 'none' | 'set' | 'decrease' | 'remove' | 'increase';

/**
 * Built from the project's own word for what it counts, so a samples project
 * reads "Sets the samples" rather than "Sets the stock". The blurbs stay
 * domain-neutral on purpose — "a physical count" and "inward / received"
 * describe the ARITHMETIC, which is the same whatever is being counted.
 */
const stockEffectOptions = (
  p: BalancePhrases,
): { value: StockEffect; label: string; blurb: string }[] => [
  { value: 'none', label: p.effectNone, blurb: p.effectNoneBlurb },
  { value: 'set', label: p.effectSet, blurb: 'A physical count. Replaces the balance and clears earlier drift' },
  // 'decrease' now means a SALE and nothing else — it is the only effect the
  // running-stock sales source reads. Anything else that leaves the shelf is
  // 'remove', which sits directly beneath it so the pair is chosen together.
  { value: 'decrease', label: p.effectDecrease, blurb: 'A sale. Counts towards sales and incentive' },
  { value: 'remove', label: p.effectRemove, blurb: p.effectRemoveBlurb },
  { value: 'increase', label: p.effectIncrease, blurb: 'Inward / received' },
];

/**
 * Whether a task is about a product, and how the promoter identifies it.
 *
 * Task-level rather than per-question: "Daily Stock of iPhone" is about a
 * product as a whole, so it is identified once and every question is answered
 * about it. 'table' renders those same questions as the columns of a grid with
 * one row per version.
 */
type ProductMode = null | 'product' | 'sku' | 'table';

const PRODUCT_MODES: { value: ProductMode; label: string; blurb: string }[] = [
  { value: null, label: 'No', blurb: 'An ordinary task' },
  { value: 'product', label: 'Yes — one product', blurb: 'They pick a product, then answer the questions about it' },
  { value: 'sku', label: 'Yes — one exact version', blurb: 'Product, then a step for each of its characteristics' },
  { value: 'table', label: 'Yes — every version, in a table', blurb: 'A row per version; this task’s questions become the columns' },
];

const questionTypes: { value: QuestionType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'single_choice', label: 'Single Choice' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'date', label: 'Date' },
  { value: 'photo', label: 'Photo' },
  { value: 'file', label: 'File Upload' },
  { value: 'rating_1_5', label: 'Rating' },
  { value: 'store_employee_picker', label: 'Store Employee' },
];

const CHOICE_TYPES: QuestionType[] = ['single_choice', 'multiple_choice', 'dropdown'];

// Question types the AI document reader can fill in from an upload. Mirrors
// EXTRACTABLE_QUESTION_TYPES in apps/api/src/services/photo-extraction.util.ts —
// keep the two in sync. Used here only to show the admin which questions will
// actually be filled, so they can see at a glance when nothing will be.
const AI_FILLABLE_TYPES: QuestionType[] = [
  'text', 'number', 'date', 'dropdown', 'single_choice', 'multiple_choice', 'rating_1_5',
];

// Per-type accent color for the left border on question cards — gives a quick
// visual cue of what each question is without reading the type tag.
const typeAccentColor: Record<QuestionType, string> = {
  text: '#94A3B8',
  number: '#3B82F6',
  dropdown: '#6366F1',
  single_choice: '#14B8A6',
  multiple_choice: '#A855F7',
  date: '#F59E0B',
  photo: '#E11D48',
  file: '#0D9488',
  rating_1_5: '#0EA5E9',
  store_employee_picker: '#22C55E',
  product_selection: '#7C3AED',
};

/**
 * The picker is not in `questionTypes`, so the type-tag lookup finds no label
 * for it. Naming it here keeps the card's tag honest instead of blank.
 */
const PRODUCT_SELECTION_TYPE: QuestionType = 'product_selection';
const isProductPicker = (q: { question_type: QuestionType }) =>
  q.question_type === PRODUCT_SELECTION_TYPE;

// Compact multi-select for role assignment. Trigger shows "All roles" or
// "{N} role(s) selected". Panel has a checkbox per role. Closes on outside
// click. No new dependency — uses a mousedown listener.
interface RolesDropdownProps {
  roles: { id: string; name: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** At least one role must be picked: no "all roles by leaving it empty", and a Select all row. */
  required?: boolean;
}
function RolesDropdown({ roles, selected, onChange, required = false }: RolesDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useOutsideClick(ref, () => setOpen(false), open);

  // Where a role must be picked there is no "all roles by leaving it empty":
  // every role is picked with Select all, and says so.
  const allPicked = roles.length > 0 && roles.every((role) => selected.includes(role.id));
  const label = selected.length === 0
    ? (required ? 'Pick at least one role' : 'All roles')
    : required && allPicked && roles.length > 1
      ? `All ${roles.length} roles selected`
      : `${selected.length} role${selected.length === 1 ? '' : 's'} selected`;

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-expanded={open}
        aria-required={required || undefined}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
      >
        <span className={selected.length === 0 ? 'text-gray-500' : 'text-gray-900'}>{label}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {roles.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500">No roles in this project</div>
          ) : (
            <>
            {required && roles.length > 1 && (
              <label className="flex items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm font-medium hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allPicked}
                  onChange={() => onChange(allPicked ? [] : roles.map((role) => role.id))}
                  className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                Select all
              </label>
            )}
            {roles.map((role) => {
              const checked = selected.includes(role.id);
              return (
                <label
                  key={role.id}
                  className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(role.id)}
                    className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                  />
                  <span className="text-gray-700">{role.name}</span>
                </label>
              );
            })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Store Visit Questionnaires are TASKS (task_type='store_visit') so that authoring them is
 * the identical experience — every answer type, option source, extraction rule and reorder
 * behaviour — rather than a copy of this page that drifts. The variant only changes what is
 * fetched, what is created, and the handful of task-only controls that make no sense for a
 * questionnaire: it is live whenever its role is signed in at a store, so it has no schedule.
 *
 * A banner-only form (task_type='banner_form') is the same again: it opens from an App Banners
 * "Fill a form" banner and nowhere else, so it has no schedule either, and the send says who
 * gets it, so it has no roles.
 */
export type TasksPageVariant = 'task' | 'store_visit' | 'banner_form';

interface TasksPageProps {
  variant?: TasksPageVariant;
  /** Drawn as a tab of another page (Store Visits Configuration): that page owns the
   *  title and its line, and the create button goes into its tab row through `actionSlot`. */
  embedded?: boolean;
  actionSlot?: HTMLElement | null;
  /** The role the list opens filtered to. */
  initialRoleFilter?: string;
  /** Told the list each time it has loaded, so the page around it can show its count. */
  onTasksLoaded?: (tasks: Task[]) => void;
}

export default function TasksPage({ variant = 'task', embedded, actionSlot, initialRoleFilter, onTasksLoaded }: TasksPageProps = {}) {
  const isBannerForm = variant === 'banner_form';
  // Not a day task: no schedule, no narrowing down, and roles are not required.
  const isVisitForm = variant === 'store_visit' || isBannerForm;
  // The sentence under the title, from the catalogue by address. (Embedded in Store
  // Visits Configuration, that page draws its own title and sentence.)
  const pageLine = usePageLine();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A link can open the list narrowed to one task (?q=<its name>, as search does);
  // `link` is set while the box still holds what a link put there.
  const [searchQuery, setSearchQuery, link] = useLinkedSearch();
  // The list is shown in two tabs by status; the role filter narrows both.
  const [activeTab, setActiveTab] = useState<TaskListTab>('active');
  const [roleFilter, setRoleFilter] = useState('');
  const [projectRoles, setProjectRoles] = useState<{ id: string; name: string }[]>([]);

  // Modal states
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  // Unified modal: Basic Details collapses/expands as a section. Expanded by
  // default for new tasks; collapsed by default when editing existing tasks.
  const [basicDetailsOpen, setBasicDetailsOpen] = useState(true);
  // Inline "+ Add question" flag — when true, renders an unsaved editor card
  // at the bottom of the questions list with editingQuestion === null.
  const [showNewQuestionForm, setShowNewQuestionForm] = useState(false);

  // Form states
  const [taskForm, setTaskForm] = useState<{
    title: string;
    description: string;
    task_type: string;
    status: TaskStatus;
    start_date?: string;
    end_date?: string;
    target_role_ids: string[];
    // Narrowing the task down within its roles: the four boxes, how the filled
    // ones combine, and hand-picked people (empty = everyone who fits).
    target_store_category_ids: number[];
    target_store_trade_ids: string[];
    target_am_codes: string[];
    target_th_codes: string[];
    target_audience_match: AudienceMatch;
    target_employee_codes: string[];
    submission_mode: SubmissionMode;
    geo_based_submission: boolean;
    is_employee_rating_task: boolean;
    // Task-level Product Master. Null = an ordinary task. This replaces a
    // per-question setting that had to be repeated on every question and reset
    // itself whenever the answer type changed.
    product_mode: ProductMode;
    product_scope_group_id: string | null;
    /** Visit questionnaires only: blocks signing out of the store until submitted. */
    required_before_signout?: boolean;
    retailer_feedback?: boolean;
  } & Partial<TaskRecurrence>>({
    title: '',
    description: '',
    task_type: 'market_working',
    status: 'active',
    target_role_ids: [],
    target_store_category_ids: [],
    target_store_trade_ids: [],
    target_am_codes: [],
    target_th_codes: [],
    target_audience_match: 'all',
    target_employee_codes: [],
    submission_mode: 'overwrite',
    geo_based_submission: false,
    is_employee_rating_task: false,
    product_mode: null,
    product_scope_group_id: null,
    recurrence_pattern: 'none',
    recurrence_interval: 1,
    recurrence_end_type: 'never',
    // Once-per-period scope — only meaningful when recurrence is weekly /
    // monthly. Default per_employee matches the 2026-05-22 migration default.
    completion_scope_dimension: 'per_employee',
  });
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    // Default to a choice type so the Options section (Static / Depends on
    // another question) is visible immediately — picking that is the primary
    // decision and should precede the Answer Type step.
    question_type: 'dropdown' as QuestionType,
    options: [''],                                           // for static list
    is_required: true,
    options_source_type: 'static' as OptionSourceType,
    options_source: null as OptionSource | null,             // from-question mapping
    product_source: null as ProductMasterSource | null,      // legacy per-question binding
    // What this question's number does to the running stock balance. Only
    // meaningful on a task that uses the Product Master — a figure with no
    // product attached has no balance to move.
    stock_effect: 'none' as StockEffect,
    visibility_rules: null as RuleGroup | null,
    // ─── Validation rules (optional) ───────────────────────────────────
    // None of these have any effect when left at their defaults — empty
    // validation produces null in the rules JSONB and the API skips it.
    validation_format: 'none' as 'none' | 'letters' | 'numbers' | 'alphanumeric' | 'email' | 'integer' | 'positive' | 'regex',
    validation_regex: '',
    validation_length_min: '' as number | '',
    validation_length_max: '' as number | '',
    validation_date_format: 'DD/MM/YYYY' as 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' | 'DD-MMM-YYYY' | 'long',
    validation_unique_scope: 'none' as 'none' | 'question' | 'task' | 'project' | 'global',
    validation_error_message: '',
    // Rating widget config — defaults match the current mobile renderer
    // (9-button half-step row on a 1–5 scale), so existing rating questions
    // don't change shape. rating_max is the admin-configurable scale ceiling
    // (min is always 1); valid range 2–10.
    rating_display_mode: 'buttons' as 'buttons' | 'stars' | 'slider' | 'dropdown',
    rating_step: 0.5 as 0.5 | 1,
    rating_max: 5 as number,
    // LEGACY single-number extraction (migration 047). Off by default, photo
    // only. Feeds the Apple Seed Score pipeline and nothing else; other
    // features also read this column as "this is the seed-score question", so
    // it is deliberately NOT the switch for the generalised reader below.
    photo_extraction_enabled: false,
    // Generalised document reader (migration 083). Opt-OUT — extraction is on
    // by default for photo/file questions, and reads the upload to prefill the
    // task's OTHER questions.
    ai_extraction_disabled: false,
    // Optional free-text nudge for an ambiguous document.
    extraction_hint: '',
    // Seed Score source flag — at most one per project. When on, the extracted
    // numeric value feeds the Apple Behavioral Scorecard's Seed Score column.
    is_seed_score_source: false,
    // AM Feedback scorecard source — at most one Rating (1–5) question per project.
    is_scorecard_rating_source: false,
    // Employee Score metric source — many allowed per project. Eligible on any
    // numeric-answer question (rating_1_5 / number / photo with extraction on).
    // The configured slabs on /reports/employee-score map the answered value
    // to points; weight is applied at compute time.
    is_scorecard_metric_source: false,
  });
  const [advancedRulesOpen, setAdvancedRulesOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [conditionalLogicOpen, setConditionalLogicOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);

  // The project's Product Master, loaded once so the question editor can offer
  // the "From Product Master" source. Left null when the project has no
  // catalogue (or the call fails) — OptionSourceEditor hides the third tile
  // entirely in that case, so the builder degrades to exactly what it was.
  const [productCatalog, setProductCatalog] = useState<EditorProductCatalog | null>(null);
  useEffect(() => {
    if (!selectedProjectId) { setProductCatalog(null); return; }
    let cancelled = false;
    productMasterAPI.getCatalog(selectedProjectId)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data as EditorProductCatalog | undefined;
        // An empty catalogue is treated as "no Product Master" so the tile does
        // not appear on projects that have never set one up.
        setProductCatalog(data && data.products.length > 0 ? data : null);
      })
      .catch(() => { if (!cancelled) setProductCatalog(null); });
    return () => { cancelled = true; };
  }, [selectedProjectId]);

  // Fetch project roles. A new project starts clean: the default tab, no role
  // filter, and none of the last project's roles left in the dropdown — a role
  // from another project would silently hide this project's tasks.
  // A task must be assigned to a role, so "no roles here", "still loading" and
  // "could not be loaded" must not look alike: only the first lets a task be
  // saved without one.
  // (Opened from a role's row on Visit targets, the list starts on that role instead.)
  const [rolesList, setRolesList] = useState<'loading' | 'ready' | 'failed'>('loading');
  useEffect(() => {
    setRoleFilter(initialRoleFilter ?? '');
    setActiveTab('active');
    setProjectRoles([]);
    setRolesList('loading');
    if (!selectedProjectId) return;
    let cancelled = false;
    rolesAPI.getByProject(selectedProjectId).then((res) => {
      if (cancelled) return;
      // An answer without a list is a failed load, not "this project has no roles".
      const roles = res.data?.data;
      if (!Array.isArray(roles)) {
        setRolesList('failed');
        return;
      }
      setProjectRoles(roles.map((r: any) => ({ id: r.id, name: r.name })));
      setRolesList('ready');
    }).catch(() => {
      if (!cancelled) setRolesList('failed');
    });
    return () => { cancelled = true; };
  }, [selectedProjectId]);

  // What a task can be limited to beyond role: the project's store categories and
  // trades, and its Assistant Managers and Trade Heads. Loaded with the page (the
  // list shows them too). Each list fails on its own: one that cannot be read is
  // simply empty, and saved values still show by their id.
  const [audienceOptions, setAudienceOptions] = useState<AudienceOptions>(NO_AUDIENCE_OPTIONS);
  // Whether each list has arrived. Until it has (or if it failed) the form must
  // not claim the project has nothing to pick, or that a saved value is gone.
  type ListState = 'loading' | 'ready' | 'failed';
  const ALL_LISTS = (state: ListState): Record<keyof Omit<AudienceOptions, 'retiredTrades'>, ListState> =>
    ({ categories: state, trades: state, ams: state, ths: state });
  const [audienceLists, setAudienceLists] = useState(ALL_LISTS('loading'));
  useEffect(() => {
    setAudienceOptions(NO_AUDIENCE_OPTIONS);
    setAudienceLists(ALL_LISTS('loading'));
    if (!selectedProjectId || isVisitForm) return;
    let cancelled = false;
    Promise.allSettled([
      categoriesAPI.getAll(selectedProjectId),
      // Retired trades too: a task that names one still enforces it, so it needs a name.
      storeTradesAPI.list(selectedProjectId, true),
      tasksAPI.getAudienceManagers(selectedProjectId),
    ]).then(([categories, trades, managers]) => {
      if (cancelled) return;
      const rows = (r: PromiseSettledResult<any>): any[] | null => {
        const data = r.status === 'fulfilled' ? r.value?.data?.data : null;
        return Array.isArray(data) ? data : null;
      };
      const people = (list: unknown) =>
        (Array.isArray(list) ? list : []).map((m: any) => ({ value: String(m.code), label: `${m.name} (${m.code})` }));
      const mgr = managers.status === 'fulfilled' ? managers.value?.data?.data : null;
      const categoryRows = rows(categories);
      const tradeRows = rows(trades);
      const tradeOption = (t: any) => ({ value: String(t.id).toLowerCase(), label: String(t.name) });
      setAudienceOptions({
        categories: (categoryRows ?? []).map((c: any) => ({ value: String(c.id), label: String(c.category_name ?? c.name ?? c.id) })),
        trades: (tradeRows ?? []).filter((t: any) => t.active !== false).map(tradeOption),
        retiredTrades: (tradeRows ?? []).filter((t: any) => t.active === false).map(tradeOption),
        ams: people(mgr?.ams),
        ths: people(mgr?.ths),
      });
      setAudienceLists({
        categories: categoryRows ? 'ready' : 'failed',
        trades: tradeRows ? 'ready' : 'failed',
        ams: mgr ? 'ready' : 'failed',
        ths: mgr ? 'ready' : 'failed',
      });
    });
    return () => { cancelled = true; };
  }, [selectedProjectId, isVisitForm]);

  // The four pickers in the task form, one per kind of condition.
  const audiencePickers: Array<{ kind: AudienceKind; label: string; options: keyof Omit<AudienceOptions, 'retiredTrades'>; hint: string; none: string }> = [
    { kind: 'category', label: 'Store category', options: 'categories', hint: 'People whose stores include one in these categories.', none: 'No store categories are set up for this project.' },
    { kind: 'trade', label: 'Store trade', options: 'trades', hint: 'People whose stores include one of these trades.', none: 'No store trades are set up for this project.' },
    { kind: 'am', label: 'Team of Assistant Manager', options: 'ams', hint: 'Everyone who reports to them, at any level. They are not included themselves.', none: 'No one in this project holds a role named AM or Assistant Manager.' },
    { kind: 'th', label: 'Team of Trade Head', options: 'ths', hint: 'Everyone who reports to them, at any level. They are not included themselves.', none: 'No one in this project holds a role named Trade Head.' },
  ];
  const pickedAudience = audienceValues(taskForm);
  const setPickedAudience = (kind: AudienceKind, values: string[]) =>
    setTaskForm((prev) => ({ ...prev, ...audienceFieldsFrom({ ...audienceValues(prev), [kind]: values }) }));
  // Who fits the roles and boxes in the form right now: the list to hand-pick
  // from. Asked of the server a moment after the form stops changing. The list
  // on screen is always for what the form says NOW: it is emptied the moment the
  // roles or boxes change, and an answer to an older question is ignored.
  // A task must name a role. The one exception is a project that is known to
  // have no roles at all; a list that is still loading or failed to load is not that.
  const roleMissing = !isVisitForm && taskForm.target_role_ids.length === 0 && !(rolesList === 'ready' && projectRoles.length === 0);
  const [fit, setFit] = useState<{ status: 'idle' | 'loading' | 'ready' | 'unresolved' | 'failed'; people: TaskAssignees['people'] }>({ status: 'idle', people: [] });
  const fitRequest = useRef(0);
  const [fitRetry, setFitRetry] = useState(0);
  // Names seen in any earlier answer, so a picked person who no longer fits
  // still shows by name rather than as a bare code.
  const knownNames = useRef(new Map<string, string>());
  const fitKey = JSON.stringify([
    taskForm.target_role_ids, taskForm.target_store_category_ids, taskForm.target_store_trade_ids,
    taskForm.target_am_codes, taskForm.target_th_codes, taskForm.target_audience_match,
  ]);
  useEffect(() => {
    const mine = ++fitRequest.current; // whatever was asked before is no longer wanted
    if (!showTaskModal || isVisitForm || !selectedProjectId) {
      setFit({ status: 'idle', people: [] });
      return;
    }
    // A role is compulsory: nothing to work out until one is picked.
    if (roleMissing) {
      setFit({ status: 'idle', people: [] });
      return;
    }
    setFit({ status: 'loading', people: [] });
    const timer = setTimeout(() => {
      tasksAPI
        .previewAudience({
          projectId: selectedProjectId,
          target_role_ids: taskForm.target_role_ids,
          ...audienceFieldsFrom(audienceValues(taskForm)),
          target_audience_match: taskForm.target_audience_match,
          // Only to be named in the answer: a picked person who no longer fits still shows by name.
          target_employee_codes: taskForm.target_employee_codes.slice(0, MAX_PICKED_EMPLOYEES),
        })
        .then((res) => {
          if (mine !== fitRequest.current) return;
          const data = res?.data?.data;
          for (const person of [...(data?.picked ?? []), ...(data?.people ?? [])]) knownNames.current.set(person.code, person.name);
          setFit(data?.resolved ? { status: 'ready', people: data.people ?? [] } : { status: 'unresolved', people: [] });
        })
        .catch(() => {
          if (mine === fitRequest.current) setFit({ status: 'failed', people: [] });
        });
    }, 500);
    return () => clearTimeout(timer);
    // fitKey stands for the six form fields it is built from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTaskModal, isVisitForm, selectedProjectId, roleMissing, fitKey, fitRetry]);

  // The picker's options: everyone who fits, and first the picked people who do
  // not (any more), marked as such. Built once per answer, not per keystroke.
  const fitCodes = useMemo(() => new Set(fit.people.map((person) => person.code)), [fit.people]);
  const pickedCodes = taskForm.target_employee_codes;
  const employeeOptions = useMemo(() => {
    const fitting = fit.people.map((person) => ({ value: person.code, label: `${person.name} (${person.code})` }));
    const others = pickedCodes
      .filter((code) => !fitCodes.has(code))
      .map((code) => {
        const name = knownNames.current.get(code);
        const who = name ? `${name} (${code})` : code;
        return { value: code, label: fit.status === 'ready' ? `${who} (does not fit now)` : who };
      });
    return [...others, ...fitting];
  }, [fit, fitCodes, pickedCodes]);
  const pickedWhoFit = pickedCodes.filter((code) => fitCodes.has(code)).length;

  // The Tasks list's "assigned employees" view: who a saved task reaches right now.
  const [assigneesTask, setAssigneesTask] = useState<Task | null>(null);
  const [assignees, setAssignees] = useState<{ status: 'loading' | 'ready' | 'unresolved' | 'failed'; people: TaskAssignees['people'] }>({ status: 'loading', people: [] });
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const assigneesRequest = useRef(0);
  const openAssignees = (task: Task) => {
    const mine = ++assigneesRequest.current;
    setAssigneesTask(task);
    setAssigneeSearch('');
    setAssignees({ status: 'loading', people: [] });
    tasksAPI
      .getAssignees(task.id)
      .then((res) => {
        if (mine !== assigneesRequest.current) return;
        const data = res?.data?.data;
        setAssignees(data?.resolved ? { status: 'ready', people: data.people ?? [] } : { status: 'unresolved', people: [] });
      })
      .catch(() => {
        if (mine === assigneesRequest.current) setAssignees({ status: 'failed', people: [] });
      });
  };
  const closeAssignees = () => {
    assigneesRequest.current += 1;
    setAssigneesTask(null);
  };

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const params: { projectId?: number; task_type?: string } = { projectId: selectedProjectId ?? undefined };
      // Without this the questionnaire page would list ordinary tasks; the API excludes
      // store-visit rows whenever no explicit type is asked for.
      if (isVisitForm) params.task_type = variant;

      const response = await tasksAPI.getAll(params);
      if (response.data.success) {
        setTasks(response.data.data);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Report the loaded list up to the page around this one. Never a list that is still
  // loading or failed to load: the last project's rows would be counted as this one's.
  useEffect(() => {
    if (!loading && !error) onTasksLoaded?.(tasks);
  }, [tasks, loading, error, onTasksLoaded]);

  // Fetch questions for a task
  const fetchQuestions = async (taskId: string) => {
    try {
      setQuestionsLoading(true);
      const response = await tasksAPI.getQuestions(taskId);
      if (response.data.success) {
        setQuestions(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching questions:', err);
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Auto-fetch questions whenever the modal is editing a real task. Clears when
  // closing or switching to a brand-new task.
  useEffect(() => {
    if (showTaskModal && editingTask?.id) {
      fetchQuestions(editingTask.id);
    } else {
      setQuestions([]);
      setShowNewQuestionForm(false);
    }
  }, [showTaskModal, editingTask?.id]);


  // Filter tasks: search and role narrow both tabs; the open tab picks the status.
  // Only a role the dropdown lists can filter — never an id it cannot show.
  const effectiveRole = projectRoles.some((r) => r.id === roleFilter) ? roleFilter : '';
  const tasksByTab = useMemo(
    () => splitTasksByStatus(tasks, { search: searchQuery, roleId: effectiveRole }),
    [tasks, searchQuery, effectiveRole],
  );
  const filteredTasks = tasksByTab[activeTab];
  const isFiltered = Boolean(searchQuery.trim() || effectiveRole);
  const listNoun = isBannerForm ? 'banner-only forms' : isVisitForm ? 'questionnaires' : 'tasks';
  const itemNoun = isBannerForm ? 'form' : isVisitForm ? 'questionnaire' : 'task';
  const createLabel = isBannerForm ? 'New form' : isVisitForm ? 'New Questionnaire' : 'Create Task';
  const otherTab: TaskListTab = activeTab === 'active' ? 'inactive' : 'active';
  const matchesOnOtherTab = tasksByTab[otherTab].length;

  // A link names one task, and search finds inactive tasks too, which the Active tab
  // the page opens on does not list. So each link opens the tab that lists the task,
  // ONCE, when the tasks are in. A link that finds nothing leaves the tab alone. After
  // that the tab is the person's again, and typing in the box never moves it.
  const tabMovedFor = useRef<SearchLink | null>(null);
  useEffect(() => {
    if (!link || tabMovedFor.current === link || loading || error) return;
    tabMovedFor.current = link;
    // The task of that name comes before any whose title or description only holds the text.
    const name = link.text.trim().toLowerCase();
    const named = tasks.filter((t) => t.title.trim().toLowerCase() === name);
    const found = splitTasksByStatus(named.length > 0 ? named : tasks, { search: link.text, roleId: '' });
    if (found.active.length > 0) setActiveTab('active');
    else if (found.inactive.length > 0) setActiveTab('inactive');
  }, [link, tasks, loading, error]);

  // Handle task form submit
  const isRecurrenceActive = taskForm.recurrence_pattern && taskForm.recurrence_pattern !== 'none';

  const handleToggleTaskStatus = async (task: Task) => {
    const nextStatus: TaskStatus = task.status === 'active' ? 'inactive' : 'active';
    try {
      await tasksAPI.update(task.id, { status: nextStatus });
      fetchTasks();
    } catch (err: any) {
      console.error('Failed to toggle task status:', err);
      // A clash under Retailer feedback says which questionnaire to untick.
      alert(err?.response?.data?.message || 'Failed to update task status');
    }
  };

  // Save Basic Details. `keepOpen` is true when saving inline from the modal's
  // Basic Details section — we want to keep the modal open and activate the
  // Questions section (using the returned task id).
  const handleTaskSubmit = async (e: React.FormEvent, keepOpen = false) => {
    e.preventDefault();
    if (!taskForm.title.trim()) {
      setFormError('Title is required');
      return;
    }

    if (isRecurrenceActive && !taskForm.start_date) {
      setFormError('Start Date is required when recurrence is enabled');
      return;
    }

    if (isRecurrenceActive) {
      const recurrenceError = validateRecurrence(taskForm);
      if (recurrenceError) {
        setFormError(recurrenceError);
        return;
      }
    }

    // A task is assigned to roles; the boxes and hand-picked people only narrow
    // it down. (A visit questionnaire may still be for every role.)
    if (!isVisitForm) {
      if (roleMissing) {
        setFormError(
          rolesList === 'loading'
            ? 'The roles are still loading. Try again in a moment.'
            : rolesList === 'failed'
              ? 'The roles could not be loaded, so a role cannot be picked. Reload the page and try again.'
              : editingTask && (editingTask.target_role_ids ?? []).length === 0
                ? 'This task currently goes to every role. Pick the roles it should go to ("Select all" keeps every current role).'
                : 'Pick at least one role in "Assign to Roles".',
        );
        return;
      }
      if (taskForm.target_employee_codes.length > MAX_PICKED_EMPLOYEES) {
        setFormError(`Pick up to ${MAX_PICKED_EMPLOYEES.toLocaleString()} employees, or leave "Specific Employees" empty to give the task to everyone who fits.`);
        return;
      }
    }

    setFormError(null);

    try {
      setActionLoading(true);
      let savedTask: Task | null = null;
      if (editingTask) {
        const res = await tasksAPI.update(editingTask.id, taskForm);
        savedTask = res?.data?.data || { ...editingTask, ...taskForm } as Task;
      } else {
        const res = await tasksAPI.create({ ...taskForm, project_id: selectedProjectId ?? undefined });
        savedTask = res?.data?.data || null;
        // A new task is active. Bring the list to where it will appear, or the
        // admin saves and sees nothing change.
        setActiveTab('active');
        const roles = taskForm.target_role_ids ?? [];
        if (roleFilter && roles.length > 0 && !roles.includes(roleFilter)) setRoleFilter('');
      }
      fetchTasks();
      if (keepOpen) {
        // Inline save — keep modal open, collapse Basic Details, activate Questions.
        if (savedTask) setEditingTask(savedTask);
        setBasicDetailsOpen(false);
        // Saving may have added or removed the product picker card. The
        // questions effect keys off editingTask.id, which does NOT change when
        // an existing task is edited, so without this the list keeps rendering
        // the pre-save set and the picker appears only after a reopen.
        if (savedTask?.id) fetchQuestions(savedTask.id);
      } else {
        setShowTaskModal(false);
        resetTaskForm();
      }
    } catch (err: any) {
      console.error('Error saving task:', err);
      // Surface what the server actually said. A bare "Failed to save task"
      // fires identically on a 400 validation message, a 403, and a 500 from a
      // missing column — so it told an admin (and whoever they reported it to)
      // nothing at all, and the only way to find the cause was DevTools.
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error;
      alert(
        serverMsg
          ? `Failed to save task\n\n${serverMsg}${status ? `\n\n(HTTP ${status})` : ''}`
          : `Failed to save task\n\n${err?.message || 'No response from the server.'}`
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Handle question form submit
  const handleQuestionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionForm.question_text.trim() || !editingTask) return;

    try {
      setActionLoading(true);
      const isChoice = CHOICE_TYPES.includes(questionForm.question_type);
      // Options: for static choice questions, send the filtered list; for
      // from-question sourced choice questions, options live in rules.option_source
      // so we send null for options. For non-choice types, no options.
      let options: string[] | undefined;
      if (isChoice && questionForm.options_source_type === 'static') {
        options = questionForm.options.filter((opt) => opt.trim());
      } else {
        options = undefined;
      }

      // Assemble rules JSONB from form state. null = no rules.
      const rules: QuestionRules = {};
      if (questionForm.visibility_rules && questionForm.visibility_rules.conditions.length > 0) {
        rules.visibility = questionForm.visibility_rules;
      }
      if (
        isChoice &&
        questionForm.options_source_type === 'from_question' &&
        questionForm.options_source &&
        questionForm.options_source.depends_on_question_id
      ) {
        // Emit the per-row {type, options?} mapping shape. Strip empty option
        // strings from choice rows; drop `options` altogether for non-choice
        // rows so the stored JSONB stays compact.
        const CHOICE = new Set(['single_choice', 'multiple_choice', 'dropdown']);
        const cleanedMapping: Record<string, any> = {};
        Object.entries(questionForm.options_source.mapping).forEach(([k, entry]: [string, any]) => {
          if (entry && typeof entry === 'object' && 'type' in entry) {
            const type = entry.type as string;
            if (CHOICE.has(type)) {
              cleanedMapping[k] = {
                type,
                options: Array.isArray(entry.options) ? entry.options.filter((o: string) => o.trim()) : [],
              };
            } else {
              cleanedMapping[k] = { type };
            }
          } else if (Array.isArray(entry)) {
            // Legacy shape encountered in local state; coerce to new shape using
            // the question's global type as the parent type fallback.
            cleanedMapping[k] = {
              type: questionForm.question_type,
              options: entry.filter((o: string) => o.trim()),
            };
          }
        });
        // Canonical new shape carries the full parent list in
        // `depends_on_question_ids`. The singular `depends_on_question_id`
        // mirrors the first parent for back-compat with older mobile readers.
        const ids = Array.isArray(questionForm.options_source.depends_on_question_ids)
          && questionForm.options_source.depends_on_question_ids.length > 0
          ? questionForm.options_source.depends_on_question_ids
          : [questionForm.options_source.depends_on_question_id];
        // Clean a MappingEntry the same way the per-row mapping above is
        // cleaned — strip empty strings, drop unused `options` on non-choice
        // rows. Used for pattern-rule entries and default fallbacks.
        const cleanEntry = (entry: any): any => {
          if (!entry || typeof entry !== 'object' || !('type' in entry)) return entry;
          const type = entry.type as string;
          if (CHOICE.has(type)) {
            return {
              type,
              options: Array.isArray(entry.options)
                ? entry.options.filter((o: string) => typeof o === 'string' && o.trim())
                : [],
            };
          }
          return { type };
        };
        // patterns_by_parent: one ordered list per text/number parent. Drop
        // rows that are structurally incomplete (no operator) so stored JSONB
        // stays clean; the editor seeds every row with a valid operator so
        // this should only fire for hand-crafted payloads.
        const cleanedPatterns: Record<string, any[]> = {};
        const srcPatterns = questionForm.options_source.patterns_by_parent || {};
        Object.entries(srcPatterns).forEach(([pid, list]) => {
          if (!ids.includes(pid) || !Array.isArray(list)) return;
          cleanedPatterns[pid] = list
            .filter((p: any) => p && typeof p === 'object' && typeof p.operator === 'string')
            .map((p: any) => {
              const out: any = { operator: p.operator, entry: cleanEntry(p.entry) };
              if (p.value !== undefined && p.value !== '') out.value = p.value;
              if (p.value_to !== undefined && p.value_to !== '') out.value_to = p.value_to;
              return out;
            });
        });
        const cleanedDefaults: Record<string, any> = {};
        const srcDefaults = questionForm.options_source.default_by_parent || {};
        Object.entries(srcDefaults).forEach(([pid, entry]) => {
          if (!ids.includes(pid)) return;
          cleanedDefaults[pid] = cleanEntry(entry);
        });
        rules.option_source = {
          depends_on_question_id: ids[0],
          depends_on_question_ids: ids,
          mapping: cleanedMapping,
          ...(Object.keys(cleanedPatterns).length > 0 ? { patterns_by_parent: cleanedPatterns } : {}),
          ...(Object.keys(cleanedDefaults).length > 0 ? { default_by_parent: cleanedDefaults } : {}),
        };
      }

      // Product Master sourcing. Written into the SAME rules.option_source key
      // as the matrix shape — the two are alternatives, never both — with
      // `type` telling the server validator and the mobile resolver which one
      // this is. Empty optional keys are omitted so the JSONB stays clean.
      // NOT gated on isChoice. A picker question's answer type is usually
      // Number ("how many did you sell?"), and requiring a choice type here
      // silently dropped the product binding on save.
      if (questionForm.options_source_type === 'product_master' && questionForm.product_source) {
        const ps = questionForm.product_source;
        const characteristic = (ps.characteristic ?? '').trim();
        rules.option_source = {
          type: 'product_master',
          level: ps.level,
          ...(ps.level === 'characteristic' && characteristic ? { characteristic } : {}),
          ...(ps.depends_on_question_id ? { depends_on_question_id: ps.depends_on_question_id } : {}),
          ...(ps.scope_group_id ? { scope_group_id: ps.scope_group_id } : {}),
        } as any;
      }

      // Stock effect. Written unconditionally when set, and cleared explicitly
      // when it is not — a COALESCE-style "omit when default" would leave a
      // stale 'decrease' on a question the admin just switched back to
      // recording-only, and the balance query would keep honouring it.
      if (questionForm.stock_effect && questionForm.stock_effect !== 'none') {
        (rules as any).stock_effect = questionForm.stock_effect;
      } else {
        delete (rules as any).stock_effect;
      }

      // Validation rules — assemble from form state. Skip every key that's
      // at its default (no rule). If everything's default, omit the
      // `validation` block entirely so the rules JSONB stays clean.
      const validation: ValidationRules = {};
      if (questionForm.validation_format && questionForm.validation_format !== 'none') {
        validation.format = questionForm.validation_format;
        if (questionForm.validation_format === 'regex' && questionForm.validation_regex.trim()) {
          validation.regex = questionForm.validation_regex.trim();
        }
      }
      const minLen =
        typeof questionForm.validation_length_min === 'number' ? questionForm.validation_length_min : null;
      const maxLen =
        typeof questionForm.validation_length_max === 'number' ? questionForm.validation_length_max : null;
      if (minLen != null || maxLen != null) {
        validation.length = { min: minLen, max: maxLen };
      }
      if (questionForm.question_type === 'date') {
        validation.dateFormat = questionForm.validation_date_format;
      }
      if (questionForm.validation_unique_scope && questionForm.validation_unique_scope !== 'none') {
        validation.unique = { scope: questionForm.validation_unique_scope };
      }
      if (questionForm.validation_error_message.trim()) {
        validation.errorMessage = questionForm.validation_error_message.trim();
      }
      if (Object.keys(validation).length > 0) {
        rules.validation = validation;
      }

      const rulesPayload = Object.keys(rules).length > 0 ? rules : null;

      const payload: any = {
        question_text: questionForm.question_text,
        question_type: questionForm.question_type,
        options,
        is_required: questionForm.is_required,
        rules: rulesPayload,
      };
      // Send rating widget config only for the rating type. For other types
      // the columns stay at their (harmless) defaults; we don't want a
      // non-rating question to overwrite the values either.
      if (questionForm.question_type === 'rating_1_5') {
        payload.rating_display_mode = questionForm.rating_display_mode;
        payload.rating_step = questionForm.rating_step;
        payload.rating_max = questionForm.rating_max;
        // `is_scorecard_rating_source` is no longer sent from this editor —
        // the column is soft-deprecated (see the editor's commentary
        // block). Omitting the key lets the backend COALESCE preserve
        // the existing value, so legacy flagged questions keep feeding
        // the AM Feedback path and new ones default to false at DB level.
      }
      // Generalised document reader — travels with every upload question.
      if (questionForm.question_type === 'photo' || questionForm.question_type === 'file') {
        payload.ai_extraction_disabled = questionForm.ai_extraction_disabled;
        payload.extraction_hint = questionForm.extraction_hint.trim() || null;
      }
      // LEGACY single-number extraction. Only sent when explicitly on, and only
      // for photo questions. Omitting the keys when it's off lets the backend
      // COALESCE preserve the existing values — important because editing an
      // unrelated field on the live Seed Score question must not silently
      // clear its flags.
      if (questionForm.question_type === 'photo' && questionForm.photo_extraction_enabled) {
        payload.photo_extraction_enabled = true;
        payload.photo_extraction_schema = { type: 'numeric' };
        payload.is_seed_score_source = questionForm.is_seed_score_source;
      }
      // `is_scorecard_metric_source` is no longer sent from this editor —
      // see the soft-deprecation comment in the JSX block above. The
      // canonical wiring is now Score → Manage → sub-metric source picker.
      // Omitting the key lets the backend COALESCE preserve whatever the
      // DB has, so legacy flagged questions keep auto-populating their
      // scorecard_metric_defs entry via syncProjectCatalog.
      if (editingQuestion) {
        try {
          await tasksAPI.updateQuestion(editingQuestion.id, payload);
        } catch (err: any) {
          if (err.response?.data?.code === 'QUESTION_HAS_ANSWERS') {
            const answerCount = err.response.data.answerCount ?? 0;
            const proceed = window.confirm(
              `This question already has ${answerCount} answer(s).\n\n` +
              `Existing answers will KEEP their original question text, type, and options — historical reports remain unchanged.\n\n` +
              `Only new submissions from this point onward will use the updated question.\n\n` +
              `Continue with the update?`
            );
            if (!proceed) {
              setActionLoading(false);
              return;
            }
            await tasksAPI.updateQuestion(editingQuestion.id, { ...payload, confirm_overwrite: true });
          } else {
            throw err;
          }
        }
      } else {
        await tasksAPI.addQuestion(editingTask.id, payload);
      }

      resetQuestionForm();
      setShowNewQuestionForm(false);
      fetchQuestions(editingTask.id);
      fetchTasks();
    } catch (err: any) {
      console.error('Error saving question:', err);
      alert(err?.response?.data?.message || err?.message || 'Failed to save question');
    } finally {
      setActionLoading(false);
    }
  };

  // Delete task
  const handleDeleteTask = async (taskId: string) => {
    try {
      setActionLoading(true);
      await tasksAPI.delete(taskId);
      setShowDeleteConfirm(null);
      fetchTasks();
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task');
    } finally {
      setActionLoading(false);
    }
  };

  // Delete question
  const handleDeleteQuestion = async (questionId: string) => {
    if (!editingTask) return;

    if (!confirm('Are you sure you want to delete this question?')) return;

    try {
      setActionLoading(true);
      await tasksAPI.deleteQuestion(questionId);
      fetchQuestions(editingTask.id);
      fetchTasks();
    } catch (err: any) {
      console.error('Error deleting question:', err);
      if (err.response?.data?.code === 'QUESTION_HAS_ANSWERS') {
        alert(`⚠️ Cannot Delete Question\n\nThis question has ${err.response.data.answerCount} existing answer(s).\n\nTo protect data integrity, questions with responses cannot be deleted. You can mark the task as inactive instead, or create a new version of the task.`);
      } else {
        alert('Failed to delete question');
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (index: number) => {
    if (!editingTask || draggedIndex === null || draggedIndex === index) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // The drag indexes refer to the SORTED order the user sees in the modal.
    // Splice the sorted copy, then renumber each question's `display_order` so
    // the next render — which re-sorts by display_order — matches what the user
    // just dragged. Without the renumber the optimistic update was silently
    // overwritten by the sort and the modal looked like drag didn't do anything.
    const sorted = [...questions].sort((a, b) => a.display_order - b.display_order);
    const [draggedItem] = sorted.splice(draggedIndex, 1);
    sorted.splice(index, 0, draggedItem);
    const renumbered = sorted.map((q, i) => ({ ...q, display_order: i }));

    // Optimistically update UI with the new order AND new display_order values.
    setQuestions(renumbered);
    setDraggedIndex(null);
    setDragOverIndex(null);

    try {
      const questionIds = renumbered.map(q => q.id);
      await tasksAPI.reorderQuestions(editingTask.id, questionIds);
    } catch (err) {
      console.error('Error reordering questions:', err);
      fetchQuestions(editingTask.id);
      alert('Failed to reorder questions');
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Reset forms
  const resetTaskForm = () => {
    setTaskForm({
      title: '',
      description: '',
      task_type: isVisitForm ? variant : 'market_working',
      status: 'active',
      target_role_ids: [],
      target_store_category_ids: [],
      target_store_trade_ids: [],
      target_am_codes: [],
      target_th_codes: [],
      target_audience_match: 'all',
      target_employee_codes: [],
      submission_mode: 'overwrite',
      geo_based_submission: false,
      is_employee_rating_task: false,
      product_mode: null,
      product_scope_group_id: null,
      recurrence_pattern: 'none',
      recurrence_interval: 1,
      recurrence_end_type: 'never',
      completion_scope_dimension: 'per_employee',
    });
    setEditingTask(null);
    setFormError(null);
  };

  const resetQuestionForm = () => {
    setQuestionForm({
      question_text: '',
      question_type: 'dropdown',
      options: [''],
      is_required: true,
      options_source_type: 'static',
      options_source: null,
      product_source: null,
      stock_effect: 'none',
      visibility_rules: null,
      validation_format: 'none',
      validation_regex: '',
      validation_length_min: '',
      validation_length_max: '',
      validation_date_format: 'DD/MM/YYYY',
      validation_unique_scope: 'none',
      validation_error_message: '',
      rating_display_mode: 'buttons',
      rating_step: 0.5,
      rating_max: 5,
      photo_extraction_enabled: false,
      is_seed_score_source: false,
      is_scorecard_rating_source: false,
      is_scorecard_metric_source: false,
      ai_extraction_disabled: false,
      extraction_hint: '',
    });
    setEditingQuestion(null);
    setConditionalLogicOpen(false);
    setAdvancedRulesOpen(false);
  };

  // Open the unified Add/Edit Task modal for editing an existing task. Basic
  // Details opens collapsed so the admin lands on the Questions section; the
  // summary row shows title / roles / recurrence at a glance.
  const openEditTask = (task: Task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || '',
      task_type: task.task_type,
      status: task.status,
      required_before_signout: (task as any).required_before_signout === true,
      retailer_feedback: (task as any).retailer_feedback === true,
      start_date: task.start_date || undefined,
      end_date: task.end_date || undefined,
      recurrence_pattern: task.recurrence_pattern || 'none',
      recurrence_interval: task.recurrence_interval || 1,
      recurrence_days_of_week: task.recurrence_days_of_week,
      recurrence_day_of_month: task.recurrence_day_of_month,
      recurrence_week_of_month: task.recurrence_week_of_month,
      recurrence_month_of_year: task.recurrence_month_of_year,
      recurrence_end_type: task.recurrence_end_type || 'never',
      recurrence_end_date: task.recurrence_end_date,
      recurrence_occurrences: task.recurrence_occurrences,
      recurrence_monthly_mode: task.recurrence_monthly_mode ?? null,
      recurrence_month_range_from: task.recurrence_month_range_from ?? null,
      recurrence_month_range_until: task.recurrence_month_range_until ?? null,
      recurrence_specific_days_of_month: task.recurrence_specific_days_of_month ?? null,
      recurrence_nth_weekdays: task.recurrence_nth_weekdays ?? null,
      target_role_ids: task.target_role_ids || [],
      ...audienceFieldsFrom(audienceValues(task)),
      target_audience_match: audienceMatchOf(task),
      target_employee_codes: pickedEmployeesOf(task),
      submission_mode: task.submission_mode === 'incremental' ? 'incremental' : 'overwrite',
      product_mode: ((task as any).product_mode ?? null) as ProductMode,
      product_scope_group_id: (task as any).product_scope_group_id ?? null,
      geo_based_submission: task.geo_based_submission === true,
      is_employee_rating_task: (task as any).is_employee_rating_task === true,
      completion_scope_dimension: task.completion_scope_dimension === 'per_store' ? 'per_store' : 'per_employee',
    });
    setBasicDetailsOpen(false);
    setShowTaskModal(true);
  };

  // Edit question
  const openEditQuestion = (question: Question) => {
    setEditingQuestion(question);
    const rules = question.rules || {};
    // Three-way now: a `type` of 'product_master' marks the catalogue-backed
    // shape; anything else with an option_source is the legacy matrix.
    const isProductSourced = (rules.option_source as any)?.type === 'product_master';
    const sourceType: OptionSourceType = isProductSourced
      ? 'product_master'
      : (rules.option_source ? 'from_question' : 'static');
    const validation = rules.validation || {};
    setQuestionForm({
      question_text: question.question_text,
      question_type: question.question_type,
      options: question.options && question.options.length > 0 ? question.options : [''],
      is_required: question.is_required,
      options_source_type: sourceType,
      // Normalize older single-parent tasks on load so the editor always
      // works with the canonical multi-parent shape (ids array + JSON-array
      // mapping keys). Writes always emit the canonical shape.
      // normalizeOptionSource only understands the matrix shape; running it on a
      // product_master source would find no mapping and null it out, silently
      // discarding the binding on the next save.
      options_source: isProductSourced ? null : (normalizeOptionSource(rules.option_source) || null),
      product_source: isProductSourced
        ? (rules.option_source as any as ProductMasterSource)
        : null,
      stock_effect: ((rules as any)?.stock_effect as StockEffect) || 'none',
      visibility_rules: rules.visibility || null,
      // Hydrate validation rules into the form fields so the editor reflects
      // what's already saved.
      validation_format: (validation.format as any) || 'none',
      validation_regex: validation.regex || '',
      validation_length_min: typeof validation.length?.min === 'number' ? validation.length.min : '',
      validation_length_max: typeof validation.length?.max === 'number' ? validation.length.max : '',
      validation_date_format: (validation.dateFormat as any) || 'DD/MM/YYYY',
      validation_unique_scope: (validation.unique?.scope as any) || 'none',
      validation_error_message: validation.errorMessage || '',
      // Rating widget config — read from row; fall back to current-renderer defaults.
      rating_display_mode: ((question as any).rating_display_mode as any) || 'buttons',
      rating_step: Number((question as any).rating_step) === 1 ? 1 : 0.5,
      rating_max: (() => {
        const n = Math.trunc(Number((question as any).rating_max));
        return Number.isFinite(n) && n >= 2 && n <= 10 ? n : 5;
      })(),
      // Photo extraction flags — hydrate from row, default off.
      photo_extraction_enabled: (question as any).photo_extraction_enabled === true,
      is_seed_score_source: (question as any).is_seed_score_source === true,
      is_scorecard_rating_source: (question as any).is_scorecard_rating_source === true,
      is_scorecard_metric_source: (question as any).is_scorecard_metric_source === true,
      // Generalised reader — opt-OUT, so an absent column means "on".
      ai_extraction_disabled: (question as any).ai_extraction_disabled === true,
      extraction_hint: (question as any).extraction_hint || '',
    });
    setConditionalLogicOpen(!!rules.visibility);
    // Auto-expand the Advanced rules section if any validation rule is set.
    setAdvancedRulesOpen(!!rules.validation);
  };

  // List of questions exposed to the RuleBuilder/OptionSourceEditor. Uses current
  // form state for the question being edited so references stay live as admin types.
  // Every phrase that names what this project counts. Derived from the
  // catalogue payload the builder already fetches, so there is no extra call.
  const phrases = useMemo(
    () => balancePhrases(productCatalog?.balanceLabel),
    [productCatalog?.balanceLabel]
  );
  const stockEffects = useMemo(() => stockEffectOptions(phrases), [phrases]);

  const availableRuleQuestions = useMemo<RuleQuestion[]>(
    () =>
      questions
        // The picker is not a condition you can branch on: its answer is a
        // product, chosen from a catalogue that changes independently of this
        // task, so any rule written against it would silently stop matching the
        // moment the catalogue moved.
        .filter((q) => !isProductPicker(q))
        .map((q) => ({
        id: q.id,
        question_text: q.question_text,
        question_type: q.question_type,
        options: q.options || [],
        // Thread option_source so the editor can treat dynamic-options
        // questions (whose top-level options are empty) as valid parents for
        // further cascading dependencies by unioning their mapping options.
        option_source: q.rules?.option_source ?? null,
      })),
    [questions]
  );

  // Inline question editor — rendered in place inside a card when that card is
  // being edited or when the admin clicked "+ Add question". Single instance
  // since only one card is ever open at a time.
  const renderQuestionEditor = (displayIndex: number) => (
    <form onSubmit={handleQuestionSubmit} className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 flex items-center justify-center rounded text-xs font-semibold bg-primary-600 text-white">
          {displayIndex}
        </span>
        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          {editingQuestion ? 'Edit question' : 'New question'}
        </span>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1.5">Question Text</label>
        <textarea
          value={questionForm.question_text}
          onChange={(e) => setQuestionForm((prev) => ({ ...prev, question_text: e.target.value }))}
          className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none bg-white"
          placeholder="Enter your question here..."
          rows={2}
          required
        />
      </div>

      {/* Options tiles come first — picking Static vs Depends-on-another-
          question is the primary decision. OptionSourceEditor renders the
          tiles plus (in Depends mode) the "Options come from" + mapping
          table. The Static-mode options list is NOT rendered here; it sits
          below the Answer Type block so the Static flow reads as
          tiles → Answer Type → options list. Gated on choice types; default
          question_type='dropdown' keeps this visible on first open. */}
      {/* Questions authored before the setting moved to the task. Their binding
          is preserved verbatim on save — this only says where the control went,
          so an admin editing an old question is not left hunting for a tile
          that no longer exists. */}
      {questionForm.options_source_type === 'product_master' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
          This question picks a product on its own. That is now set once for the
          whole task — open <strong>Basic Details → Is this task about a product?</strong>{' '}
          and every question is answered against the product chosen there.
        </div>
      )}

      {CHOICE_TYPES.includes(questionForm.question_type)
        && questionForm.options_source_type !== 'product_master' && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">Options</label>
          <OptionSourceEditor
            sourceType={questionForm.options_source_type}
            onSourceTypeChange={(t) => setQuestionForm((prev) => ({ ...prev, options_source_type: t }))}
            source={questionForm.options_source}
            onSourceChange={(src) => setQuestionForm((prev) => ({ ...prev, options_source: src }))}
            currentQuestionType={questionForm.question_type as any}
            availableQuestions={availableRuleQuestions}
            currentQuestionId={editingQuestion?.id || null}
          />
        </div>
      )}

      {/* Answer Type: hidden when options come from another question (the
          type is picked per mapping row above, so a top-level selector is
          redundant). Visible in Static mode so admins can pick text, number,
          date, photo, or a choice type. */}
      {!(
        CHOICE_TYPES.includes(questionForm.question_type) &&
        questionForm.options_source_type === 'from_question'
      ) && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">Answer Type</label>
          <select
            value={questionForm.question_type}
            onChange={(e) => {
              const nextType = e.target.value as QuestionType;
              setQuestionForm((prev) => ({
                ...prev,
                question_type: nextType,
                options_source_type: CHOICE_TYPES.includes(nextType) ? prev.options_source_type : 'static',
                options_source: CHOICE_TYPES.includes(nextType) ? prev.options_source : null,
              }));
            }}
            className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
          >
            {questionTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Running stock. Folded away by default: it is a separate feature most
          tasks never use, and putting its vocabulary in the main flow made a
          plain "type a number" question look complicated. It opens by itself
          when already set, so an existing setting is never hidden.

          Only offered on a product task — a number with no product attached has
          no balance to move — and only on a number, because that is what the
          balance query reads.

          ⚠️ DELIBERATELY NOT GATED ON stockTrackingEnabled. It was, and that
          was a deadlock: the flag has no UI anywhere (it is read-only, see
          product-master.controller.ts), so it is false until someone runs SQL —
          which meant the control never rendered, no question could ever declare
          an effect, and the feature could never be switched on usefully. The
          flag governs whether BALANCES ARE SHOWN to the promoter, not whether
          an admin may record what a question means. Declaring intent early is
          harmless: a stock_effect with tracking off moves nothing. */}
      {taskForm.product_mode && questionForm.question_type === 'number' && (
        <details className="rounded-lg border border-gray-200 bg-white p-2.5" open={questionForm.stock_effect !== 'none'}>
          <summary className="cursor-pointer list-none text-xs text-gray-600 hover:text-gray-800">
            <span className="underline decoration-dotted underline-offset-2">
              {phrases.trackWithThis}
            </span>
            {questionForm.stock_effect !== 'none' && (
              <span className="ml-1.5 rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-semibold text-primary-700">
                {stockEffects.find((s) => s.value === questionForm.stock_effect)?.label}
              </span>
            )}
          </summary>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <select
              value={questionForm.stock_effect}
              onChange={(e) => setQuestionForm((prev) => ({
                ...prev,
                stock_effect: e.target.value as StockEffect,
              }))}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px]"
            >
              {stockEffects.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <span className="text-[10px] italic text-gray-400">
              {stockEffects.find((s) => s.value === questionForm.stock_effect)?.blurb}
            </span>
          </div>
        </details>
      )}

      {/* Static options list — rendered AFTER Answer Type so the Static flow
          reads: tiles → Answer Type → options list. Only shown in Static mode
          for choice-type answers. */}
      {CHOICE_TYPES.includes(questionForm.question_type) &&
        questionForm.options_source_type === 'static' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              {questionForm.question_type === 'dropdown' ? 'Dropdown options'
                : questionForm.question_type === 'single_choice' ? 'Single-choice options'
                : 'Multiple-choice options'}
            </label>
            <StaticOptionsEditor
              options={questionForm.options}
              onChange={(opts) => setQuestionForm((prev) => ({ ...prev, options: opts }))}
            />
          </div>
        )}

      <div className="flex items-center gap-3 py-1">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={questionForm.is_required}
            onChange={(e) => setQuestionForm((prev) => ({ ...prev, is_required: e.target.checked }))}
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
          <span className="ml-2 text-sm text-gray-700">Required</span>
        </label>
      </div>

      {/* Hint + widget config for the two Employee Rating Answer Types — explains
          the constrained UX so admins know what to expect on mobile. Rating
          additionally lets admins pick the scale max, picker style and step
          granularity. */}
      {questionForm.question_type === 'rating_1_5' && (
        <div className="space-y-2 rounded-md border border-primary-200 bg-primary-50/40 p-3">
          <p className="text-xs text-primary-900">
            Mobile shows a constrained 1–{questionForm.rating_max} picker. Pick the scale, widget style and step the AM will use to score.
          </p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Max rating</label>
              <input
                type="number"
                min={2}
                max={10}
                step={1}
                value={questionForm.rating_max}
                onChange={(e) => {
                  const n = Math.trunc(Number(e.target.value));
                  setQuestionForm((prev) => ({
                    ...prev,
                    rating_max: Number.isFinite(n) ? Math.min(10, Math.max(2, n)) : 5,
                  }));
                }}
                className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Picker style</label>
              <select
                value={questionForm.rating_display_mode}
                onChange={(e) =>
                  setQuestionForm((prev) => ({
                    ...prev,
                    rating_display_mode: e.target.value as 'buttons' | 'stars' | 'slider' | 'dropdown',
                  }))
                }
                className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs"
              >
                <option value="buttons">Buttons</option>
                <option value="stars">Stars</option>
                <option value="slider">Slider</option>
                <option value="dropdown">Dropdown</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Step</label>
              <select
                value={String(questionForm.rating_step)}
                onChange={(e) =>
                  setQuestionForm((prev) => ({
                    ...prev,
                    rating_step: e.target.value === '1' ? 1 : 0.5,
                  }))
                }
                className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs"
              >
                <option value="0.5">Half steps (1, 1.5, …)</option>
                <option value="1">Whole numbers (1, 2, 3, …)</option>
              </select>
            </div>
          </div>
          {/* The legacy per-rating "Use in Apple Scorecard (AM Feedback)"
              checkbox was retired alongside the metric-source one
              (soft-deprecated 2026-05-30). A rating question is now
              wired into Employee Score by selecting it as the source
              of a Custom-metric sub-metric — same flow as numeric /
              photo questions. `is_scorecard_rating_source` still gates
              the legacy AM Feedback path in the engine; existing
              flagged rows keep working unchanged. */}
        </div>
      )}
      {questionForm.question_type === 'store_employee_picker' && (
        <p className="rounded-md border border-primary-200 bg-primary-50/40 p-2 text-xs text-primary-900">
          Mobile shows a <strong>single-select</strong> dropdown auto-populated with employees of the AM's current store —
          the AM picks one employee per submission (not multiple). The selected employee's code is stored as the answer.
        </p>
      )}

      {/* AI document reader (migration 083). ON by default for every upload
          question: when an employee uploads a photo or PDF, the server reads it
          and prefills THIS TASK'S OTHER QUESTIONS for them to verify. The task's
          questions are the extraction spec, so there is nothing to configure —
          which is why this box is an opt-OUT plus an optional hint.

          The "will fill" preview is the important part of this UI: it makes the
          server-side cost gate visible. A task with no fillable questions makes
          no AI call at all, and the admin can see that here rather than
          wondering why nothing happens in the field. */}
      {(questionForm.question_type === 'photo' || questionForm.question_type === 'file') && (() => {
        const fillable = questions.filter(
          (q) => q.id !== editingQuestion?.id
            && AI_FILLABLE_TYPES.includes(q.question_type as QuestionType)
        );
        const on = !questionForm.ai_extraction_disabled;
        return (
          <div className="space-y-2 rounded-md border border-violet-200 bg-violet-50/40 p-3">
            <div className="flex items-start gap-2">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-violet-600" />
              <div className="flex-1">
                <div className="text-xs font-medium text-gray-800">Read this upload with AI</div>
                <p className="mt-0.5 text-[11px] text-gray-600">
                  On by default. When someone uploads here, we read the image or PDF and fill in the
                  other questions in this task automatically. They can check and change anything
                  before submitting.
                </p>
              </div>
            </div>

            {on && (
              fillable.length > 0 ? (
                <p className="text-[11px] text-violet-900">
                  <span className="font-medium">Will try to fill:</span>{' '}
                  {fillable.slice(0, 6).map((q) => q.question_text).join(', ')}
                  {fillable.length > 6 ? ` +${fillable.length - 6} more` : ''}
                </p>
              ) : (
                <p className="text-[11px] text-gray-600">
                  No other questions in this task can be filled from a document yet, so nothing will
                  be read. Add a text, number, date or choice question and it will be filled in
                  automatically.
                </p>
              )
            )}

            {on && (
              <div>
                <label className="block text-[11px] font-medium text-gray-700">
                  What should we look for? <span className="font-normal text-gray-500">(optional)</span>
                </label>
                <input
                  type="text"
                  maxLength={280}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-xs"
                  placeholder="e.g. the invoice grand total, not the subtotal"
                  value={questionForm.extraction_hint}
                  onChange={(e) =>
                    setQuestionForm((prev) => ({ ...prev, extraction_hint: e.target.value }))
                  }
                />
              </div>
            )}

            <label className="flex cursor-pointer items-center gap-2 pt-1">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 cursor-pointer accent-violet-600"
                checked={questionForm.ai_extraction_disabled}
                onChange={(e) =>
                  setQuestionForm((prev) => ({ ...prev, ai_extraction_disabled: e.target.checked }))
                }
              />
              <span className="text-[11px] text-gray-700">Don't read this one</span>
            </label>

            {questionForm.question_type === 'file' && on && (
              <p className="text-[11px] text-gray-500">
                PDFs and images can be read. Word and Excel files are stored but not read.
              </p>
            )}
          </div>
        );
      })()}

      {/* LEGACY single-number extraction (migration 047) — feeds the Apple Seed
          Score pipeline. Superseded by the reader above for everything else, and
          left visible only because the one project still using it must be able
          to see and unset it. `photo_extraction_enabled` is also read elsewhere
          as "this is the seed-score question" (target-metrics eligibility, the
          Apple Configure-tab relevance probe), which is exactly why the new
          reader got its own flag instead of reusing this one. */}
      {questionForm.question_type === 'photo' && (
        <details className="rounded-md border border-gray-200 bg-gray-50/60 p-3">
          <summary className="cursor-pointer text-xs font-medium text-gray-700">
            Legacy: extract a single number (Apple Seed Score)
          </summary>
          <label className="mt-2 flex cursor-pointer items-start justify-between gap-3">
            <p className="text-[11px] text-gray-600">
              Reads one headline number from the image and stores it against the answer for the
              Apple Behavioral Scorecard. Only the Seed Score question needs this — everything else
              is handled by the AI reader above.
            </p>
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 cursor-pointer accent-primary-600"
              checked={questionForm.photo_extraction_enabled}
              onChange={(e) =>
                setQuestionForm((prev) => ({
                  ...prev,
                  photo_extraction_enabled: e.target.checked,
                }))
              }
            />
          </label>
        </details>
      )}

      {/* Employee Score metric source — opts the numeric answer into the
          project's Employee Score (see /reports/employee-score). Eligible
          on any question type whose answer can be parsed as numeric:
          rating_1_5 / number / photo-with-extraction / dropdown /
          single_choice. The last two are common when admins model a 1-5
          rating as a numeric-option dropdown; the engine's loader filters
          answer_text via a numeric regex so non-numeric options score 0
          safely. Unlike Seed Score / AM Feedback, any number of questions
          per project can carry this flag — each becomes its own configurable
          metric with weight + slabs on the Configure tab.

          ── Soft-deprecated 2026-05-30 ──
          The per-question "Use in Employee Score" checkbox was retired
          once the Custom-metric sub-metric source picker became the
          canonical way to wire a question into Employee Score (Manage
          Metrics → sub-metric → source: From task question). The
          `is_scorecard_metric_source` column still exists; the engine
          and `syncProjectCatalog` still respect it for already-flagged
          questions so existing scorecards keep working. New questions
          can no longer set the flag from this editor — they're wired
          via the Score page's Manage tab instead. */}

      <div className="border-t border-gray-200 pt-3">
        <button
          type="button"
          onClick={() => setConditionalLogicOpen((v) => !v)}
          className="w-full flex items-center gap-2 text-left group"
        >
          {conditionalLogicOpen ? (
            <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-primary-600 transition-colors" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-primary-600 transition-colors" />
          )}
          <Zap className="h-3.5 w-3.5 text-amber-500" />
          <span className="text-xs font-semibold text-gray-700 group-hover:text-primary-700 transition-colors">
            Conditional logic
          </span>
          <span className="ml-auto text-[11px]">
            {questionForm.visibility_rules && questionForm.visibility_rules.conditions.length > 0 ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 font-medium border border-amber-200">
                Rule configured
              </span>
            ) : (
              <span className="text-gray-400">Optional</span>
            )}
          </span>
        </button>
        {conditionalLogicOpen && (
          <div className="mt-3">
            <RuleBuilder
              rule={questionForm.visibility_rules}
              onChange={(next) => setQuestionForm((prev) => ({ ...prev, visibility_rules: next }))}
              availableQuestions={availableRuleQuestions}
              currentQuestionId={editingQuestion?.id || null}
            />
          </div>
        )}
      </div>

      {/* ─── Advanced rules (validation: format / length / uniqueness / date display) ───
          Collapsed by default. Expanded automatically when editing a
          question that already has validation rules set. */}
      {(() => {
        const qt = questionForm.question_type;
        const showFormatLength = qt === 'text' || qt === 'number';
        const showDateFormat = qt === 'date';
        const showUnique = qt !== 'photo' && qt !== 'file';
        // Photo & File questions get nothing here — Advanced rules section hides itself.
        if (!showFormatLength && !showDateFormat && !showUnique) return null;

        const hasAnyRule =
          (questionForm.validation_format && questionForm.validation_format !== 'none') ||
          questionForm.validation_length_min !== '' ||
          questionForm.validation_length_max !== '' ||
          (questionForm.validation_unique_scope && questionForm.validation_unique_scope !== 'none') ||
          questionForm.validation_error_message.trim() !== '';

        const formatOptions: { value: string; label: string }[] = qt === 'number'
          ? [
              { value: 'none', label: 'None' },
              { value: 'integer', label: 'Integer (whole numbers only)' },
              { value: 'positive', label: 'Positive numbers only' },
              { value: 'regex', label: 'Custom regex' },
            ]
          : [
              { value: 'none', label: 'None' },
              { value: 'letters', label: 'Letters only' },
              { value: 'numbers', label: 'Numbers only' },
              { value: 'alphanumeric', label: 'Alphanumeric' },
              { value: 'email', label: 'Email' },
              { value: 'regex', label: 'Custom regex' },
            ];

        return (
          <div className="border-t border-gray-200 pt-3">
            <button
              type="button"
              onClick={() => setAdvancedRulesOpen((v) => !v)}
              className="w-full flex items-center gap-2 text-left group"
            >
              {advancedRulesOpen ? (
                <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-primary-600 transition-colors" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-primary-600 transition-colors" />
              )}
              <span className="text-xs font-semibold text-gray-700 group-hover:text-primary-700 transition-colors">
                Advanced rules
              </span>
              <span className="ml-auto text-[11px]">
                {hasAnyRule ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 px-2 py-0.5 font-medium border border-indigo-200">
                    Rule configured
                  </span>
                ) : (
                  <span className="text-gray-400">Optional</span>
                )}
              </span>
            </button>

            {advancedRulesOpen && (
              <div className="mt-3 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                {showFormatLength && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Format</label>
                      <select
                        value={questionForm.validation_format}
                        onChange={(e) =>
                          setQuestionForm((prev) => ({
                            ...prev,
                            validation_format: e.target.value as any,
                          }))
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {formatOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {questionForm.validation_format === 'regex' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Regex pattern
                        </label>
                        <input
                          type="text"
                          value={questionForm.validation_regex}
                          onChange={(e) =>
                            setQuestionForm((prev) => ({
                              ...prev,
                              validation_regex: e.target.value,
                            }))
                          }
                          placeholder="e.g. ^[A-Z0-9]{14}$"
                          className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <p className="text-[11px] text-gray-500 mt-1">
                          JavaScript regex without the leading/trailing slashes. Anchors (^ $) recommended.
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Min length
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={questionForm.validation_length_min}
                          onChange={(e) =>
                            setQuestionForm((prev) => ({
                              ...prev,
                              validation_length_min: e.target.value === '' ? '' : Number(e.target.value),
                            }))
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="—"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Max length
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={questionForm.validation_length_max}
                          onChange={(e) =>
                            setQuestionForm((prev) => ({
                              ...prev,
                              validation_length_max: e.target.value === '' ? '' : Number(e.target.value),
                            }))
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="—"
                        />
                      </div>
                    </div>
                  </>
                )}

                {showDateFormat && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Display format
                    </label>
                    <select
                      value={questionForm.validation_date_format}
                      onChange={(e) =>
                        setQuestionForm((prev) => ({
                          ...prev,
                          validation_date_format: e.target.value as any,
                        }))
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="DD/MM/YYYY">DD/MM/YYYY (e.g. 08/05/2026)</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY (e.g. 05/08/2026)</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD (e.g. 2026-05-08)</option>
                      <option value="DD-MMM-YYYY">DD-MMM-YYYY (e.g. 08-May-2026)</option>
                      <option value="long">8 May 2026 (friendly)</option>
                    </select>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Storage stays as YYYY-MM-DD; this controls how the date is displayed in the
                      app and reports.
                    </p>
                  </div>
                )}

                {showUnique && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Must be unique
                    </label>
                    <select
                      value={questionForm.validation_unique_scope}
                      onChange={(e) =>
                        setQuestionForm((prev) => ({
                          ...prev,
                          validation_unique_scope: e.target.value as any,
                        }))
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="none">No</option>
                      <option value="question">Yes — within this question</option>
                      <option value="task">Yes — within this task</option>
                      <option value="project">Yes — within the project</option>
                      <option value="global">Yes — across all projects</option>
                    </select>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Two users can't submit the same value within the chosen scope.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Custom error message <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={questionForm.validation_error_message}
                    onChange={(e) =>
                      setQuestionForm((prev) => ({
                        ...prev,
                        validation_error_message: e.target.value,
                      }))
                    }
                    placeholder="Shown to the user when any of the rules above fail"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          onClick={() => { resetQuestionForm(); setShowNewQuestionForm(false); }}
          className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        {editingQuestion && (
          <button
            type="button"
            onClick={() => handleDeleteQuestion(editingQuestion.id)}
            className="px-3 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          >
            Delete
          </button>
        )}
        <button
          type="submit"
          disabled={actionLoading || !questionForm.question_text.trim()}
          className="ml-auto px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {actionLoading ? (
            <Loader2 className="w-4 h-4 animate-spin mx-auto" />
          ) : editingQuestion ? (
            'Update Question'
          ) : (
            'Add Question'
          )}
        </button>
      </div>
    </form>
  );

  const createButton = (
    <button
      onClick={() => {
        resetTaskForm();
        setBasicDetailsOpen(true);
        setShowTaskModal(true);
      }}
      className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
    >
      <Plus className="w-4 h-4 inline-block mr-2" />
      {createLabel}
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Header. Embedded, the page around this one owns the title and its line, and
          the create button sits in that page's tab row. */}
      {embedded ? (
        actionSlot && createPortal(createButton, actionSlot)
      ) : (
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{isVisitForm ? 'Store Visit Questionnaire' : 'Tasks'}</h1>
            {pageLine && <p className="mt-1 truncate text-sm text-gray-500" title={pageLine}>{pageLine}</p>}
          </div>
          {createButton}
        </div>
      )}

      {/* Tabs: active and inactive, each with the number that matches the filters.
          The same strip every master page uses (Stores, Employees, Product Master):
          light tray, white pill for the open tab, icon then label. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1" role="tablist" aria-label="Status">
          {([
            { key: 'active', label: 'Active', icon: ClipboardCheck },
            { key: 'inactive', label: 'Inactive', icon: ClipboardX },
          ] as { key: TaskListTab; label: string; icon: typeof ClipboardCheck }[]).map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                  isActive ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {!loading && !error ? ` (${tasksByTab[tab.key].length.toLocaleString()})` : ''}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content Card */}
      <section className="rounded-lg border border-gray-100 bg-white p-6">
        {/* Filters */}
        <SearchFilterBar
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`Search ${listNoun}...`}
        >
          {!isBannerForm && <select
            aria-label="Filter by role"
            value={effectiveRole}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 sm:w-56"
          >
            <option value="">All roles</option>
            {projectRoles.map((role) => (
              <option key={role.id} value={role.id}>{role.name}</option>
            ))}
          </select>}
        </SearchFilterBar>

        {/* Content */}
        {loading ? (
          <ContentLoadingState message={`Loading ${listNoun}...`} />
        ) : error ? (
          <div className="text-center py-12">
            <AlertCircle className="w-8 h-8 mx-auto mb-4 text-red-500" />
            <p className="text-gray-500">{error}</p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardList className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {isFiltered ? `No ${activeTab} ${listNoun} match` : `No ${activeTab} ${listNoun}`}
            </h3>
            <p className="text-gray-500 mb-4">
              {isFiltered
                ? matchesOnOtherTab > 0
                  ? `Nothing on this tab matches, but the ${otherTab === 'active' ? 'Active' : 'Inactive'} tab has ${matchesOnOtherTab.toLocaleString()}.`
                  : 'Try adjusting your search or role filter'
                : tasks.length === 0
                  ? `Create your first ${itemNoun} to get started`
                  : activeTab === 'active'
                    ? 'Everything here has been deactivated. See the Inactive tab.'
                    : 'Deactivated items appear here.'}
            </p>
            {isFiltered && matchesOnOtherTab > 0 && (
              <button
                type="button"
                onClick={() => setActiveTab(otherTab)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100"
              >
                Show on the {otherTab === 'active' ? 'Active' : 'Inactive'} tab
              </button>
            )}
            {!isFiltered && tasks.length === 0 && (
              <button
                onClick={() => {
                  resetTaskForm();
                  setBasicDetailsOpen(true);
                  setShowTaskModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
              >
                <Plus className="w-4 h-4" />
                {createLabel}
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/70 border-b border-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Title</th>
                    {!isBannerForm && (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Assigned to</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Recurrence</th>
                      </>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Questions</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Updated</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTasks.map((task) => {
                    return (
                      <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900 text-sm">{task.title}</p>
                              {variant === 'store_visit' && (task as any).retailer_feedback === true && (
                                <span className="inline-flex items-center rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                                  Retailer feedback
                                </span>
                              )}
                              {task.submission_mode === 'incremental' && (
                                <span
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                                  title="Incremental — submissions aggregate across the day"
                                >
                                  🔁 Incremental
                                </span>
                              )}
                            </div>
                            {task.description && (
                              <p className="text-xs text-gray-500 truncate max-w-xs" title={task.description}>
                                {task.description}
                              </p>
                            )}
                          </div>
                        </td>
                        {!isBannerForm && (
                        <>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {/* The roles ("All roles" on an older task that names none), then
                                what narrows them down: one chip per filled box, the full
                                list on hover, led by how they combine when there is more
                                than one, and how many people are hand-picked. */}
                            {(!task.target_role_ids || task.target_role_ids.length === 0) ? (
                              <span className="text-xs text-gray-400">All roles</span>
                            ) : (
                              task.target_role_ids.map((rid: string) => {
                                const role = projectRoles.find(r => r.id === rid);
                                return (
                                  <span key={rid} className="px-1.5 py-0.5 text-xs font-medium rounded bg-primary-50 text-primary-700">
                                    {role?.name || rid.slice(0, 8)}
                                  </span>
                                );
                              })
                            )}
                            {audienceJoinLabel(task) && (
                              <span className="self-center text-xs text-gray-500">{audienceJoinLabel(task)}</span>
                            )}
                            {describeTaskAudience(task, audienceOptions).map((chip) => (
                              <span
                                key={chip.kind}
                                title={chip.detail}
                                className="px-1.5 py-0.5 text-xs font-medium rounded border border-gray-200 bg-gray-50 text-gray-700"
                              >
                                {chip.label}
                              </span>
                            ))}
                            {pickedEmployeesOf(task).length > 0 && (
                              <span
                                title="Hand-picked people: only they get the task. Open Assigned Employees to see who."
                                className="px-1.5 py-0.5 text-xs font-medium rounded border border-amber-200 bg-amber-50 text-amber-800"
                              >
                                Only {pickedEmployeesOf(task).length} picked
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Repeat className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-xs">{formatRecurrenceText(task)}</span>
                          </div>
                        </td>
                        </>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <MessageSquare className="w-4 h-4 text-gray-400" />
                            <span>{task.question_count}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-500">{task.created_at ? new Date(task.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-500">{(task as any).updated_at ? new Date((task as any).updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <TableActionGroup>
                            {!isBannerForm && (
                              <TableActionButton action="candidates" label="Assigned Employees"
                                onClick={() => openAssignees(task)}
                                title="Assigned Employees" />
                            )}
                            <TableActionButton action="edit" label={isBannerForm ? 'Edit form' : 'Edit Task'}
                              onClick={() => openEditTask(task)}
                              title={isBannerForm ? 'Edit form' : 'Edit Task'} />
                            <TableActionButton action="delete" label={isBannerForm ? 'Delete form' : 'Delete Task'}
                              onClick={() => setShowDeleteConfirm(task.id)}
                              title={isBannerForm ? 'Delete form' : 'Delete Task'} />
                            <TableActionButton action={task.status === 'active' ? 'deactivate' : 'activate'} label={task.status === 'active' ? 'Click to deactivate' : 'Click to activate'}
                              type="button"
                              role="switch"
                              aria-checked={task.status === 'active'}
                              onClick={() => handleToggleTaskStatus(task)}
                              title={task.status === 'active' ? 'Click to deactivate' : 'Click to activate'} />
                          </TableActionGroup>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Task Modal */}
      {showTaskModal && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden max-h-[90vh]">
            {/* Header */}
            <div className="bg-primary-600 px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-primary-200 text-xs uppercase tracking-wide mb-0.5">{isBannerForm ? 'Banner-only forms' : 'Task Management'}</p>
                  <h3 className="text-lg font-semibold text-white">
                    {isBannerForm ? (editingTask ? 'Edit form' : 'New form') : editingTask ? 'Edit Task' : 'Create New Task'}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowTaskModal(false);
                    resetTaskForm();
                  }}
                  className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5 bg-gray-50/40">
              {/* ========= Section A: Basic Details (collapsible) ========= */}
              <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
                <button
                  type="button"
                  onClick={() => setBasicDetailsOpen((v) => !v)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100"
                >
                  {basicDetailsOpen ? (
                    <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  )}
                  <span className="text-sm font-semibold text-gray-900">Basic Details</span>
                  {!basicDetailsOpen && (
                    <span className="text-xs text-gray-500 truncate text-left">
                      {taskForm.title || 'Untitled task'}
                      {' · '}
                      {(taskForm.target_role_ids || []).length > 0
                        ? `${(taskForm.target_role_ids || []).length} role(s)`
                        : isVisitForm || (editingTask && (editingTask.target_role_ids ?? []).length === 0) ? 'All roles' : 'No role picked'}
                      {countAudienceKinds(taskForm) > 0 &&
                        `, ${countAudienceKinds(taskForm)} filter(s)${
                          countAudienceKinds(taskForm) > 1 ? (audienceMatchOf(taskForm) === 'any' ? ' (any one is enough)' : ' (all must fit)') : ''
                        }`}
                      {taskForm.target_employee_codes.length > 0 && `, ${taskForm.target_employee_codes.length} picked`}
                      {' · '}
                      {formatRecurrenceText(taskForm as any)}
                    </span>
                  )}
                  {editingTask && (
                    <span className="ml-auto text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5 flex-shrink-0">
                      Saved
                    </span>
                  )}
                </button>
                {basicDetailsOpen && (
                  <form onSubmit={(e) => handleTaskSubmit(e, true)} className="p-4 space-y-4" id="task-form">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={taskForm.title}
                        onChange={(e) => setTaskForm((prev) => ({ ...prev, title: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder={isBannerForm ? 'Enter form title' : 'Enter task title'}
                        required
                      />
                    </div>

                    {/* Submission Mode — two-card radio */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Submission Mode</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setTaskForm((prev) => ({ ...prev, submission_mode: 'overwrite' }))}
                          className={`text-left rounded-lg border-2 p-3 transition-all ${
                            taskForm.submission_mode === 'overwrite'
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-base">📝</span>
                            <span className="text-sm font-semibold text-gray-900">Overwrite &amp; Submit</span>
                            {taskForm.submission_mode === 'overwrite' && (
                              <span className="ml-auto text-[11px] font-semibold text-primary-700">Selected</span>
                            )}
                          </div>
                          <p className="text-[11px] leading-relaxed text-gray-600">
                            Latest submission wins. Employees can edit and resubmit throughout the day.
                          </p>
                        </button>
                        <button
                          type="button"
                          onClick={() => setTaskForm((prev) => ({ ...prev, submission_mode: 'incremental' }))}
                          className={`text-left rounded-lg border-2 p-3 transition-all ${
                            taskForm.submission_mode === 'incremental'
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-base">🔁</span>
                            <span className="text-sm font-semibold text-gray-900">Incremental</span>
                            {taskForm.submission_mode === 'incremental' && (
                              <span className="ml-auto text-[11px] font-semibold text-primary-700">Selected</span>
                            )}
                          </div>
                          <p className="text-[11px] leading-relaxed text-gray-600">
                            Each submission adds to the day's totals. Reports sum numbers, gather photos, list texts.
                          </p>
                        </button>
                      </div>
                    </div>

                    {/* Task-level Product Master. Sits here, next to Submission
                        Mode, because it is a property of the TASK — "this task is
                        about a product" — not of each question. It used to be a
                        per-question setting, which meant repeating the same
                        decision on every question and losing it whenever the
                        answer type changed. */}
                    {productCatalog && (
                      <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
                        <div>
                          <label className="text-sm font-medium text-gray-700">
                            Is this task about a product?
                          </label>
                          <p className="mt-0.5 text-xs text-gray-500">
                            The promoter identifies it once, then answers every question about it.
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {PRODUCT_MODES.map((m) => {
                            const active = (taskForm.product_mode ?? null) === m.value;
                            return (
                              <button
                                key={String(m.value)}
                                type="button"
                                onClick={() => setTaskForm((prev) => ({
                                  ...prev,
                                  product_mode: m.value,
                                  // A scope means nothing without a mode.
                                  product_scope_group_id: m.value ? prev.product_scope_group_id : null,
                                }))}
                                className={`rounded-md border px-2.5 py-2 text-left transition-all ${
                                  active
                                    ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-200'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                              >
                                <div className="text-[11px] font-semibold text-gray-900">{m.label}</div>
                                <div className="mt-0.5 text-[10px] leading-tight text-gray-500">{m.blurb}</div>
                              </button>
                            );
                          })}
                        </div>
                        {taskForm.product_mode && (
                          <div>
                            <label htmlFor="task-product-scope" className="mb-1 block text-xs font-medium text-gray-600">
                              Limit to (optional)
                            </label>
                            <select
                              id="task-product-scope"
                              value={taskForm.product_scope_group_id ?? ''}
                              onChange={(e) => setTaskForm((prev) => ({
                                ...prev, product_scope_group_id: e.target.value || null,
                              }))}
                              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs"
                            >
                              <option value="">— Whole catalogue —</option>
                              {productCatalog.groups.map((g) => (
                                <option key={g.id} value={g.id}>{g.path}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Geo-based submission toggle — per-task override of where the user
                        must physically be to submit. When on, the mobile must be inside
                        one of the task's target store radii. When off, anywhere is OK. */}
                    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3">
                      <div className="pr-4">
                        <label className="text-sm font-medium text-gray-700">Require store presence to submit</label>
                        <p className="mt-0.5 text-xs text-gray-500">
                          When on, this task can only be submitted while inside the store radius
                          (employee location is recorded). When off, submission is allowed from anywhere.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setTaskForm((prev) => ({ ...prev, geo_based_submission: !prev.geo_based_submission }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${taskForm.geo_based_submission ? 'bg-primary-600' : 'bg-gray-200'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${taskForm.geo_based_submission ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>

                    {/* Employee Rating task flag — available to every project. When on,
                        ratings collected via this task become eligible source data
                        for Employee Score metrics (the admin wires each rating into
                        Score on the Manage tab via a Custom-metric sub-metric).
                        Shape (≥1 Rating (1–5) + one Store Employee question) is
                        enforced only at activation; admins can build the task in any
                        order. The legacy AM Feedback path (is_scorecard_rating_source)
                        still works for already-flagged questions but is no longer
                        surfaced from this editor — see soft-deprecation note above. */}
                    {/* "Employee Rating task" toggle retired — AM Feedback is now
                        configured in Employee Score via a custom task-question
                        metric (with a "Rated employee from" picker), not a
                        task-level flag. */}

                    {/* Assign to Roles + Recurrence pattern dropdown share a
                        single row. The Recurrence dropdown is rendered here
                        rather than inside RecurrenceSelector — that component
                        below renders only the pattern-specific details
                        (Repeat On chips / Monthly modes / Start Date row)
                        via the hidePatternPicker prop. Both call the same
                        defaultsForPattern helper so the write-back is
                        identical. */}
                    <div className={`grid gap-3 ${(projectRoles.length > 0 || rolesList !== 'ready') && !isVisitForm ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      {!isVisitForm && rolesList !== 'ready' && (
                        <div>
                          <p className="block text-sm font-medium text-gray-700 mb-1">Assign to Roles <span className="text-red-500">*</span></p>
                          <p className="rounded-lg border border-dashed border-gray-200 px-3 py-2 text-xs text-gray-500">
                            {rolesList === 'loading' ? <InlineLoader label="Loading roles…" size="xs" /> : 'The roles could not be loaded. Reload the page to retry.'}
                          </p>
                        </div>
                      )}
                      {projectRoles.length > 0 && !isBannerForm && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Assign to Roles {!isVisitForm && <span className="text-red-500">*</span>}
                          </label>
                          <RolesDropdown
                            roles={projectRoles}
                            selected={taskForm.target_role_ids || []}
                            onChange={(next) => {
                              setFormError(null);
                              setTaskForm((prev) => ({ ...prev, target_role_ids: next }));
                            }}
                            required={!isVisitForm}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {isVisitForm
                              ? 'Leave all unselected to show task to all roles'
                              : 'Everyone in these roles gets the task, unless you narrow it down below.'}
                          </p>
                        </div>
                      )}
                      {/* A visit questionnaire is live whenever its role is signed in at a
                          store, so it has no schedule — and with the pattern left at 'none'
                          every downstream recurrence control stays hidden too. */}
                      {!isVisitForm && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Recurrence</label>
                        <select
                          value={taskForm.recurrence_pattern || 'none'}
                          onChange={(e) => {
                            const next = e.target.value as RecurrencePattern;
                            const defaults = defaultsForPattern(next, taskForm.start_date);
                            setTaskForm((prev) => ({ ...prev, ...defaults }));
                          }}
                          className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                        >
                          {(['none', 'daily', 'weekly', 'monthly'] as RecurrencePattern[]).map((pattern) => (
                            <option key={pattern} value={pattern}>
                              {pattern.charAt(0).toUpperCase() + pattern.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                      )}

                      {variant === 'store_visit' && (
                        <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-gray-200 p-3">
                          <input
                            type="checkbox"
                            checked={taskForm.required_before_signout === true}
                            onChange={(e) => setTaskForm((prev) => ({ ...prev, required_before_signout: e.target.checked }))}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600"
                          />
                          <span>
                            <span className="block text-sm font-medium text-gray-800">Must be completed before sign-out</span>
                            <span className="block text-xs text-gray-500">Signing out of the store is blocked until this is submitted for that visit.</span>
                          </span>
                        </label>
                      )}

                      {variant === 'store_visit' && (
                        <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-gray-200 p-3">
                          <input
                            type="checkbox"
                            checked={taskForm.retailer_feedback === true}
                            onChange={(e) => setTaskForm((prev) => ({ ...prev, retailer_feedback: e.target.checked }))}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600"
                          />
                          <span>
                            <span className="block text-sm font-medium text-gray-800">Show under Retailer feedback</span>
                            <span className="block text-xs text-gray-500">These questions appear in the visit agenda&rsquo;s Retailer feedback, not under Visit Tasks. One questionnaire per role.</span>
                          </span>
                        </label>
                      )}
                    </div>

                    {/* Narrowing the task down within its roles. Not for visit
                        questionnaires: those are offered by role, at the store being visited. */}
                    {!isVisitForm && (
                      <div className="space-y-3 rounded-lg border border-gray-200 p-3">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Narrow Down Within These Roles (optional)</p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            Leave every box as &ldquo;Not used&rdquo; to give the task to everyone in the roles above. Within one
                            box, any of the picked values will do. Someone not deployed to any store (usually a manager) is
                            judged by the stores of their team.
                          </p>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {audiencePickers.map((picker) => {
                            const options = audienceOptions[picker.options];
                            const picked = pickedAudience[picker.kind];
                            const list = audienceLists[picker.options];
                            const note = (text: string, stillLoading = false) => (
                              <p className="rounded-lg border border-dashed border-gray-200 px-3 py-2 text-xs text-gray-500">
                                {stillLoading ? <InlineLoader label={text} size="xs" /> : text}
                              </p>
                            );
                            return (
                              <div key={picker.kind}>
                                <label htmlFor={`task-audience-${picker.kind}`} className="mb-1 block text-sm font-medium text-gray-700">
                                  {picker.label}
                                </label>
                                {list === 'loading' ? (
                                  note('Loading…', true)
                                ) : picked.length === 0 && list === 'failed' ? (
                                  note('This list could not be loaded. Reload the page to retry.')
                                ) : picked.length === 0 && options.length === 0 ? (
                                  note(picker.none)
                                ) : (
                                  <>
                                    <MultiSearchableSelect
                                      id={`task-audience-${picker.kind}`}
                                      values={picked}
                                      onChange={(values) => setPickedAudience(picker.kind, values)}
                                      options={optionsWithPicked(options, picked, retiredOptionsOf(audienceOptions, picker.options), list === 'ready')}
                                      placeholder="Not used"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                      {list === 'failed' ? 'This list could not be loaded; what is already picked is kept.' : picker.hint}
                                    </p>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* How the filled boxes combine: asked only when more than one is
                            filled. Same two-card choice as Submission Mode. */}
                        {countAudienceKinds(taskForm) > 1 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">More Than One Box Is Filled: A Person Must Fit</label>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              aria-pressed={taskForm.target_audience_match !== 'any'}
                              onClick={() => setTaskForm((prev) => ({ ...prev, target_audience_match: 'all' }))}
                              className={`text-left rounded-lg border-2 p-3 transition-all ${
                                taskForm.target_audience_match !== 'any' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center gap-1.5 mb-1">
                                <span className="text-sm font-semibold text-gray-900">Match All</span>
                                {taskForm.target_audience_match !== 'any' && (
                                  <span className="ml-auto text-[11px] font-semibold text-primary-700">Selected</span>
                                )}
                              </div>
                              <p className="text-[11px] leading-relaxed text-gray-600">
                                A person must fit every box you fill in. Category L0 with trade WC means people at L0
                                stores of trade WC.
                              </p>
                            </button>
                            <button
                              type="button"
                              aria-pressed={taskForm.target_audience_match === 'any'}
                              onClick={() => setTaskForm((prev) => ({ ...prev, target_audience_match: 'any' }))}
                              className={`text-left rounded-lg border-2 p-3 transition-all ${
                                taskForm.target_audience_match === 'any' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center gap-1.5 mb-1">
                                <span className="text-sm font-semibold text-gray-900">Match Any</span>
                                {taskForm.target_audience_match === 'any' && (
                                  <span className="ml-auto text-[11px] font-semibold text-primary-700">Selected</span>
                                )}
                              </div>
                              <p className="text-[11px] leading-relaxed text-gray-600">
                                Fitting any one box is enough. Category L0 with trade WC means people at any L0 store,
                                plus people at any WC store.
                              </p>
                            </button>
                          </div>
                        </div>
                        )}

                        {/* Hand-picked people, from whoever fits the roles and boxes above. */}
                        <div className="border-t border-gray-100 pt-3">
                          <label htmlFor="task-audience-employees" className="mb-1 block text-sm font-medium text-gray-700">
                            Specific Employees (optional)
                          </label>
                          {roleMissing ? (
                            <p className="rounded-lg border border-dashed border-gray-200 px-3 py-2 text-xs text-gray-500">
                              Pick a role above first.
                            </p>
                          ) : (
                            <>
                              <MultiSearchableSelect
                                id="task-audience-employees"
                                values={pickedCodes}
                                onChange={(values) => {
                                  setFormError(null);
                                  setTaskForm((prev) => ({ ...prev, target_employee_codes: values }));
                                }}
                                options={employeeOptions}
                                placeholder="Everyone who fits"
                              />
                              {fit.status === 'ready' && pickedCodes.length > 0 && pickedWhoFit === 0 ? (
                                <p role="alert" className="mt-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                  None of the {pickedCodes.length} picked fit these roles and filters, so nobody would get this task.
                                  Pick from the {fit.people.length} who fit, or clear the picks.
                                </p>
                              ) : (
                                <p className="mt-1 text-xs text-gray-500" aria-live="polite">
                                  {fit.status === 'loading' && 'Working out who fits…'}
                                  {fit.status === 'ready' && pickedCodes.length === 0 &&
                                    `${fit.people.length} ${fit.people.length === 1 ? 'person fits' : 'people fit'} now. Leave this empty to give the task to all of them and to anyone who fits later, or pick names to give it only to them.`}
                                  {fit.status === 'ready' && pickedCodes.length > 0 && pickedWhoFit === pickedCodes.length &&
                                    (pickedWhoFit === fit.people.length
                                      ? `All ${fit.people.length} who fit are picked. Leaving this empty does the same, and also covers anyone who fits later.`
                                      : `Only the ${pickedCodes.length} picked get the task, out of ${fit.people.length} who fit.`)}
                                  {fit.status === 'ready' && pickedCodes.length > 0 && pickedWhoFit > 0 && pickedWhoFit < pickedCodes.length &&
                                    `${pickedWhoFit} of the ${pickedCodes.length} picked fit and get the task; ${pickedCodes.length - pickedWhoFit} no longer ${pickedCodes.length - pickedWhoFit === 1 ? 'fits' : 'fit'}.`}
                                  {(fit.status === 'unresolved' || fit.status === 'failed') && (
                                    <>
                                      Could not work out who fits just now. The task can still be saved.{' '}
                                      <button type="button" onClick={() => setFitRetry((n) => n + 1)} className="font-medium text-primary-600 hover:underline">
                                        Try again
                                      </button>
                                    </>
                                  )}
                                  {fit.status === 'idle' && 'Leave empty to give the task to everyone who fits.'}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Completion scope — only meaningful for weekly / monthly
                        recurrence. Drives whether the once-per-period rule is
                        evaluated across all submissions (per_employee) or one
                        submission per assigned store (per_store). Visit-style
                        tasks like AM/TH Visits typically want per_store. */}
                    {(taskForm.recurrence_pattern === 'weekly' || taskForm.recurrence_pattern === 'monthly') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Completion Scope</label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setTaskForm((prev) => ({ ...prev, completion_scope_dimension: 'per_employee' }))}
                            className={`text-left rounded-lg border-2 p-3 transition-all ${
                              taskForm.completion_scope_dimension === 'per_employee' || !taskForm.completion_scope_dimension
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className="text-base">👤</span>
                              <span className="text-sm font-semibold text-gray-900">Per Employee</span>
                              {(taskForm.completion_scope_dimension === 'per_employee' || !taskForm.completion_scope_dimension) && (
                                <span className="ml-auto text-[11px] font-semibold text-primary-700">Selected</span>
                              )}
                            </div>
                            <p className="text-[11px] leading-relaxed text-gray-600">
                              One submission anywhere fulfils the {taskForm.recurrence_pattern === 'weekly' ? 'week' : 'month'}.
                            </p>
                          </button>
                          <button
                            type="button"
                            onClick={() => setTaskForm((prev) => ({ ...prev, completion_scope_dimension: 'per_store' }))}
                            className={`text-left rounded-lg border-2 p-3 transition-all ${
                              taskForm.completion_scope_dimension === 'per_store'
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className="text-base">🏪</span>
                              <span className="text-sm font-semibold text-gray-900">Per Store</span>
                              {taskForm.completion_scope_dimension === 'per_store' && (
                                <span className="ml-auto text-[11px] font-semibold text-primary-700">Selected</span>
                              )}
                            </div>
                            <p className="text-[11px] leading-relaxed text-gray-600">
                              One submission per assigned store per {taskForm.recurrence_pattern === 'weekly' ? 'week' : 'month'}.
                            </p>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Recurrence details — the pattern picker itself lives
                        in the top-row grid alongside Assign to Roles.
                        hidePatternPicker tells RecurrenceSelector to render
                        only the pattern-specific options (Repeat On chips
                        for Weekly, the three Monthly mode radios, plus the
                        Start Date / Repeat Every / End Date row). */}
                    <div>
                      <RecurrenceSelector
                        variant="compact"
                        hidePatternPicker
                        value={taskForm}
                        onChange={(recurrence) => setTaskForm((prev) => ({ ...prev, ...recurrence }))}
                        startDate={taskForm.start_date}
                        startDateSlot={
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Start Date <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              value={taskForm.start_date || ''}
                              onChange={(e) => {
                                setTaskForm((prev) => ({ ...prev, start_date: e.target.value }));
                                if (formError) setFormError(null);
                              }}
                              className={`w-full h-10 px-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                                !taskForm.start_date && formError
                                  ? 'border-red-400 bg-red-50'
                                  : 'border-gray-300'
                              }`}
                            />
                          </div>
                        }
                      />
                      {formError && (
                        <p role="alert" className="mt-1 text-xs text-red-600">{formError}</p>
                      )}
                    </div>

                    {/* Inline save for Basic Details — keeps modal open and activates Questions */}
                    <div className="flex items-center justify-end pt-3 border-t border-gray-100">
                      <button
                        type="submit"
                        disabled={actionLoading || !taskForm.title.trim()}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                      >
                        {actionLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : editingTask ? (
                          'Save Changes'
                        ) : (
                          'Save Basic Details'
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </section>

              {/* ========= Section B: Questions ========= */}
              <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
                  <MessageSquare className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-900">Questions</span>
                  <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                    {questions.length}
                  </span>
                  <button
                    type="button"
                    disabled={!editingTask || showNewQuestionForm || !!editingQuestion}
                    onClick={() => {
                      resetQuestionForm();
                      setShowNewQuestionForm(true);
                    }}
                    className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title={!editingTask ? 'Save basic details first' : undefined}
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add question
                  </button>
                </div>

                <div className="p-4">
                  {/* Employee-Rating readiness banner retired alongside the
                      task-level rating flag — wire AM Feedback on the Employee
                      Score → Manage tab (custom task-question metric). */}
                  {!editingTask ? (
                    <div className="py-10 text-center text-sm text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p>Save basic details to start adding questions.</p>
                    </div>
                  ) : questionsLoading ? (
                    <BlockLoader label="Loading questions..." className="py-10" />
                  ) : questions.length === 0 && !showNewQuestionForm ? (
                    <div className="py-10 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                      <h5 className="text-sm font-medium text-gray-700 mb-1">No questions yet</h5>
                      <p className="text-xs text-gray-500">Click &ldquo;Add question&rdquo; to start.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {[...questions].sort((a, b) => a.display_order - b.display_order).map((question, index) => {
                        const isEditingThis = editingQuestion?.id === question.id;
                        const rules = question.rules || {};
                        const hasVisibility = !!rules.visibility && rules.visibility.conditions?.length > 0;
                        const hasDynamicOptions = !!rules.option_source;
                        const accent = typeAccentColor[question.question_type] || '#94A3B8';
                        const sourceQuestion = hasDynamicOptions
                          ? questions.find((q) => q.id === rules.option_source!.depends_on_question_id)
                          : null;
                        const visibilitySummary = hasVisibility
                          ? summarizeRule(rules.visibility, availableRuleQuestions)
                          : '';

                        // The product picker. Draggable like any other card and
                        // nothing else — its wording, answer type and position in
                        // the flow are the task's product setting, not a question
                        // to author. Rendered before the edit branch because it
                        // can never be the one being edited.
                        if (isProductPicker(question)) {
                          return (
                            <div
                              key={question.id}
                              draggable
                              onDragStart={() => handleDragStart(index)}
                              onDragOver={(e) => handleDragOver(e, index)}
                              onDragLeave={handleDragLeave}
                              onDrop={() => handleDrop(index)}
                              onDragEnd={handleDragEnd}
                              style={{ borderLeftColor: accent, borderLeftWidth: 3 }}
                              className={`pl-4 pr-4 py-3 rounded-lg border transition-all cursor-grab active:cursor-grabbing ${
                                dragOverIndex === index
                                  ? 'border-primary-400 border-dashed bg-primary-50'
                                  : draggedIndex === index
                                  ? 'opacity-50 border-gray-300'
                                  : 'border-violet-200 bg-violet-50/40 hover:border-violet-300'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 flex items-center gap-2 pt-0.5">
                                  <GripVertical className="w-4 h-4 text-gray-400" />
                                  <span className="w-6 h-6 flex items-center justify-center rounded text-xs font-semibold bg-violet-600 text-white">
                                    {index + 1}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-900 leading-relaxed">
                                    {question.question_text}
                                  </p>
                                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                    <span
                                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded text-white"
                                      style={{ backgroundColor: accent }}
                                    >
                                      <Package className="w-3 h-3" />
                                      Product Master
                                    </span>
                                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                                      Added automatically
                                    </span>
                                  </div>
                                  <p className="mt-1.5 text-xs text-gray-500">
                                    Drag this to where the product should be identified. It is
                                    skipped automatically when every question below it is hidden —
                                    so a &ldquo;did you sell anything?&rdquo; question placed above
                                    it will stop the flow on &ldquo;No&rdquo;.
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        if (isEditingThis) {
                          return (
                            <div
                              key={question.id}
                              style={{ borderLeftColor: accent, borderLeftWidth: 3 }}
                              className="pl-4 pr-4 py-4 rounded-lg border-2 border-primary-500 bg-white shadow-sm"
                            >
                              {renderQuestionEditor(index + 1)}
                            </div>
                          );
                        }

                        return (
                          <div
                            key={question.id}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={() => handleDrop(index)}
                            onDragEnd={handleDragEnd}
                            style={{ borderLeftColor: accent, borderLeftWidth: 3 }}
                            // bg-white sits in the branches that want it, not on the base: beside
                            // bg-primary-50 it won (Tailwind emits it later), so the drop target
                            // got its dashed border but never its fill.
                            className={`pl-4 pr-4 py-3 rounded-lg border transition-all cursor-grab active:cursor-grabbing ${
                              dragOverIndex === index
                                ? 'border-primary-400 border-dashed bg-primary-50'
                                : draggedIndex === index
                                ? 'opacity-50 border-gray-300 bg-white'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                <div className="flex-shrink-0 flex items-center gap-2 pt-0.5">
                                  <GripVertical className="w-4 h-4 text-gray-400" />
                                  <span className="w-6 h-6 flex items-center justify-center rounded text-xs font-semibold bg-primary-600 text-white">
                                    {index + 1}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-900 leading-relaxed">
                                    {question.question_text}
                                  </p>
                                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                    <span
                                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded text-white"
                                      style={{ backgroundColor: accent }}
                                    >
                                      {question.question_type === 'photo' && <Camera className="w-3 h-3" />}
                                      {question.question_type === 'file' && <Paperclip className="w-3 h-3" />}
                                      {questionTypes.find((t) => t.value === question.question_type)?.label}
                                    </span>
                                    {question.is_required ? (
                                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded">
                                        Required
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                                        Optional
                                      </span>
                                    )}
                                    {(question.question_type === 'photo' || question.question_type === 'file')
                                      && (question as any).ai_extraction_disabled !== true && (
                                      <span
                                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-violet-100 text-violet-800 rounded"
                                        title={(question as any).extraction_hint
                                          || 'Uploads here are read with AI to fill the other questions'}
                                      >
                                        <Sparkles className="w-3 h-3" />
                                        AI reads this
                                      </span>
                                    )}
                                    {hasVisibility && (
                                      <span
                                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded"
                                        title={`Shows when: ${visibilitySummary}`}
                                      >
                                        <Zap className="w-3 h-3" />
                                        Conditional
                                      </span>
                                    )}
                                    {hasDynamicOptions && (
                                      <span
                                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-800 rounded"
                                        title={sourceQuestion ? `Options from "${sourceQuestion.question_text}"` : 'Dynamic options'}
                                      >
                                        <ArrowRightLeft className="w-3 h-3" />
                                        Dynamic options
                                      </span>
                                    )}
                                  </div>
                                  {question.options && question.options.length > 0 && !hasDynamicOptions && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                      {question.options.map((opt, i) => (
                                        <span key={i} className="inline-flex items-center px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                                          {opt}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => { setShowNewQuestionForm(false); openEditQuestion(question); }}
                                  className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                                  title="Edit"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteQuestion(question.id)}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {showNewQuestionForm && !editingQuestion && (
                        <div className="pl-4 pr-4 py-4 rounded-lg border-2 border-dashed border-primary-400 bg-primary-50/30">
                          {renderQuestionEditor(questions.length + 1)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </section>
            </div>

            {/* Fixed Footer — Close only. All saves happen inline per section. */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex-shrink-0">
              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowTaskModal(false);
                    resetTaskForm();
                    resetQuestionForm();
                    setShowNewQuestionForm(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {assigneesTask && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="task-assignees-title"
            onKeyDown={(e) => { if (e.key === 'Escape') closeAssignees(); }}
            className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden max-h-[90vh]"
          >
            <div className="bg-primary-600 px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-primary-200 text-xs uppercase tracking-wide mb-0.5">Assigned Employees</p>
                  <h3 id="task-assignees-title" className="text-lg font-semibold text-white truncate">{assigneesTask.title}</h3>
                </div>
                <button
                  onClick={closeAssignees}
                  aria-label="Dismiss"
                  className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {assignees.status === 'loading' && <p className="text-sm text-gray-500"><InlineLoader label="Working out who this task is for…" /></p>}
              {(assignees.status === 'unresolved' || assignees.status === 'failed') && (
                <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  Could not work out who this task is for just now.{' '}
                  <button type="button" onClick={() => openAssignees(assigneesTask)} className="font-medium underline">
                    Try again
                  </button>
                </p>
              )}
              {assignees.status === 'ready' && (() => {
                const total = assignees.people.length;
                const picked = pickedEmployeesOf(assigneesTask).length;
                const term = assigneeSearch.trim().toLowerCase();
                const matching = term
                  ? assignees.people.filter((person) =>
                      `${person.name} ${person.code} ${person.roles.join(' ')}`.toLowerCase().includes(term))
                  : assignees.people;
                const shown = matching.slice(0, ASSIGNEE_ROWS_SHOWN);
                const count = `${total} ${total === 1 ? 'employee' : 'employees'}`;
                return (
                  <>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="text-sm text-gray-700">
                        {assigneesTask.status === 'active'
                          ? <>This task is for <span className="font-semibold">{count}</span>.</>
                          : <>Switched off, so nobody sees it. When on, it is for <span className="font-semibold">{count}</span>.</>}
                      </p>
                      {total > 0 && (
                        <input
                          type="search"
                          value={assigneeSearch}
                          onChange={(e) => setAssigneeSearch(e.target.value)}
                          placeholder="Search name, code or role"
                          aria-label="Search assigned employees"
                          className="w-64 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      )}
                    </div>
                    {picked > 0 && (
                      <p className={`rounded-lg border px-3 py-2 text-xs ${total < picked ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                        {total === picked
                          ? `Hand-picked: only the ${picked} picked ${picked === 1 ? 'person gets' : 'people get'} this task.`
                          : total === 0
                            ? `${picked === 1 ? '1 person was hand-picked for this task, but does not fit' : `${picked} people were hand-picked for this task, but none of them fit`} its roles and filters right now. Edit the task to change who is picked.`
                            : `${picked} people were hand-picked for this task; ${total} of them ${total === 1 ? 'fits' : 'fit'} its roles and filters right now.`}
                      </p>
                    )}
                    {total === 0 ? (
                      picked === 0 && <p className="text-sm text-gray-500">Nobody fits this task's roles and filters at the moment.</p>
                    ) : matching.length === 0 ? (
                      <p className="text-sm text-gray-500">No one matches that search.</p>
                    ) : (
                      <>
                        <div className="overflow-hidden rounded-lg border border-gray-200">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600">
                              <tr>
                                <th className="px-4 py-2">Employee</th>
                                <th className="px-4 py-2">Code</th>
                                <th className="px-4 py-2">Role</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {shown.map((person) => (
                                <tr key={person.code}>
                                  <td className="px-4 py-2 text-gray-900">{person.name}</td>
                                  <td className="px-4 py-2 text-gray-600">{person.code}</td>
                                  <td className="px-4 py-2 text-gray-600">{person.roles.join(', ') || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {matching.length > shown.length && (
                          <p className="text-xs text-gray-500">
                            Showing {shown.length} of {matching.length.toLocaleString()}. Search to narrow down.
                          </p>
                        )}
                      </>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex-shrink-0">
              <div className="flex items-center justify-between gap-4">
                <p className="text-xs text-gray-500">
                  Worked out now from the roles people hold in this project, their stores and their reporting line.
                </p>
                <button
                  type="button"
                  autoFocus
                  onClick={closeAssignees}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showDeleteConfirm && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{isBannerForm ? 'Delete form' : 'Delete Task'}</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this {isBannerForm ? 'form' : 'task'}? This will also delete all questions and answers associated with it.
            </p>
            <ConfirmDeleteActions
              onCancel={() => setShowDeleteConfirm(null)}
              onConfirm={() => handleDeleteTask(showDeleteConfirm)}
              loading={actionLoading}
              spinnerMode="replace"
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
